import { ITableColumns, ITableDataSource } from 'models/table';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import React from 'react';
import {
  EAddressAssetAmount,
  ELegalAttachmentsGroupBy,
  IDocument,
  IILegalEntityLicenceAttachment,
  ILegalAttachmentItem,
  ILegalEntity,
  ILegalEntityAncillaries,
  ILegalEntityAncillaryGroup,
  ILegalEntityContactWithAssociation,
  ILegalEntityExtendedAddress,
  ILegalEntityFirearm,
  ILegalEntityGroup,
  ILegalEntityGroupFirearm,
  ILegalEntityLicence,
  ILegalEntityPreviousNamesSurnames,
} from 'models/legalEntity';
import { IOrganizationLegalEntity } from 'models/organizationLegalEntity';
import { findInDictionary, IParsedDictionary, parseDictionary } from 'helpers/dictionary';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { colors } from 'theme/colors';
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { ELEAccordionsPageTypes } from 'components/organisms/LEAccordions';
import { IFirearm } from 'models/firearm';
import { EPermission } from 'models/permissions';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { getColumnTitleFromCustomField } from 'utils/table';
import { formatDate, formatDateTimeToIso, leftDays } from 'utils/date';
import {
  defaultCheckboxStyle,
  StyledCheckboxBackgroundWrapper,
} from 'components/molecules/FormCheckbox/styles';
import { IAncillary } from 'models/ancillaries';
import { EArtAncillaryType, EArtAncillaryTypeNameLowerCase } from 'constants/ArtAccessoryType';
import { IDictionary } from 'models/dictionary';
import { Link } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { getAncillaryType } from 'pages/LegalEntitiesPage/ViewLegalEntityPage';
import Icon from 'components/atoms/Icon/Icon';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatGPSField } from 'utils/form';
import Tooltip from 'components/atoms/Tooltip';
import { IAccountResponse } from 'models/account';

import { appendExtensionToFilename, appendExtensionToFilenameFromMimeType } from 'utils/files';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { getLicenceStatusChipVariant } from './LegalEntityViewLicencePage/helpers';
import { CaseStatus, ICaseForLe } from 'models/cases';
import { MenuButton } from 'components/molecules/MenuButton';
import { ICustomFieldForm } from './IndividualSearchForm';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';

export const ATTACHMENT_TYPE_LICENCE = 'LIC';
export const ATTACHMENT_TYPE_CASE = 'CAS';

export const getAddressDeletionAlertMessage = (
  isLinkedToLicence: boolean,
  hasFirearms: boolean,
  hasAncillaries: boolean,
): string => {
  if (isLinkedToLicence && (hasFirearms || hasAncillaries)) {
    return 'legalEntity.address.linkedToAssetsAndLicence.message';
  }
  if (isLinkedToLicence) {
    return 'legalEntity.address.linkedToLicence.message';
  }
  if (hasFirearms || hasAncillaries) {
    return 'legalEntity.address.linkedToFirearmOrAncillary.message';
  }
  return '';
};

export const handleAddressDeletionCheck = (
  addressId: string,
  legalEntityAddress: ILegalEntityExtendedAddress[],
  linkResult: { linked: boolean } | null | undefined,
) => {
  const addressToDelete = legalEntityAddress.find((addr) => addr.uniqueId === addressId);
  const hasFirearms = addressToDelete?.firearmAmount !== EAddressAssetAmount.NONE;
  const hasAncillaries = addressToDelete?.ancillaryAmount !== EAddressAssetAmount.NONE;
  const isLinkedToLicence = linkResult?.linked;

  return {
    shouldShowAlert: isLinkedToLicence || hasFirearms || hasAncillaries,
    alertMessage: getAddressDeletionAlertMessage(
      !!isLinkedToLicence,
      hasFirearms,
      hasAncillaries,
    ),
  };
};

export enum EProcessTypes {
  APPLICATION = 'APPLICATION',
  RENEWAL = 'RENEWAL',
  VARIATION = 'VARIATION',
}

export enum ELicenceStatusIds {
  ACTIVE = 'cab1065b-4e32-4e40-85b5-c89c6e2f0c9c',
  SUSPENDED = 'c57d7dbf-ebe1-40f2-9401-3162f497b575',
  REVOKED = '60255535-0f63-4567-a649-c22c6381711c',
  EXPIRED = 'eb320c46-e222-408b-8601-acea1c17f4e3',
  SCHEDULED = '88154f33-be25-4f82-95dc-e3da605172a1',
  SURRENDERED = 'beb02a6e-7828-4c5a-b78a-b8635bd2ccc7',
  CANCELLED = 'dd030b9b-b540-4f6e-a746-17897ec20ee6',
}

export enum ELicenceStatus {
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  SUSPENDED = 'SUSPENDED',
  REVOKED = 'REVOKED',
  EXPIRED = 'EXPIRED',
  SURRENDERED = 'SURRENDERED',
  CANCELLED = 'CANCELLED',
}

export enum EApplicationStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN PROGRESS',
  REFUSED = 'REFUSED',
}

export enum ELicenceHistoryActions {
  VARY = 'VARY',
  REVOKE = 'REVOKE',
  CREATE = 'CREATE',
  ACTIVATE = 'ACTIVATE',
  EXPIRE = 'EXPIRE',
  RENEW = 'RENEW',
  SUSPEND = 'SUSPEND',
  SURRENDER = 'SURRENDER',
  REACTIVATE = 'REACTIVATE',
  UPDATE_PAYMENT_STATUS = 'UPDATE_PAYMENT_STATUS',
  SCHEDULED_LICENCE_RENOVATION = 'SCHEDULED_LICENCE_RENOVATION',
  CANCEL = 'CANCEL',
}

export enum EPaymentStatus {
  PAID = 'PAID',
  UNPAID = 'UNPAID',
  REFUNDED = 'REFUNDED',
}

export enum EPaymentStatusId {
  PAID = 'e2c39630-a75f-485c-9462-719c1bf20928',
  UNPAID = 'b2e79d4e-9a98-4243-9ffd-a7152d1ea688',
  REFUNDED = 'e078ab67-57b3-4bb1-a1ea-fcb18001615e',
}

interface IAncillariesTableColumns {
  handleView: (id: string, type: string) => void;
  callback: any;
  selectAllCallback: () => void;
  areAllAncillariesSelected: boolean;
  selectedAncillaries: IAncillary[];
  renderStateFieldColor: (arg: ITableDataSource) => any;
  selectedAncillaryType: EArtAncillaryType;
}

interface ILegalEntityTableColumns {
  handleView?: (arg: string) => void;
  handleEdit?: (arg: string) => void;
  handleDelete?: (arg: string) => void;
  handleSelect?: (arg: string) => void;
  type: ELEAccordionsPageTypes;
  customFields?: ICustomFieldByObjectType[];
  t?: (key: string) => string;
}

interface ILegalEntityFirearmTableColumns {
  renderStateFieldColor: (arg: ITableDataSource) => any;
  type: string | null;
  showAction: boolean | null;
  t: (key: string) => string;
  hasWeaponLicenceModule: boolean | null;
}

interface ILegalEntityAncillaryTableColumns {
  renderStateFieldColor: (arg: ITableDataSource) => any;
  showDetail: boolean | null;
  t: (key: string) => string;
}

interface ILegalEntityAncillaryGroupTableColumns {
  handleAddToGroup: (arg: ITableDataSource) => void;
  handleEdit: (groupId: string) => void;
  handleDelete: (groupId: string) => void;
  renderStateFieldColor: (arg: ITableDataSource) => any;
  type: string | null;
  id: string | undefined;
  showAction: boolean | null;
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
}

interface ILegalEntityAncillaryGroupAncillariesTableColumns {
  hideViewButton: boolean;
  handleDelete: (ancillaryId: string) => void;
  ancillaryOpenDetail?: string;
  setAncillaryOpenDetail?: (ancillaryId: string) => void;
}

interface ILegalEntityAddGroupFirearmTableColumns {
  handleDelete: (arg: string) => void;
  renderStateFieldColor: (arg: ITableDataSource) => any;
}

interface ILegalEntityGroupTableColumns {
  handleAdd: (item: ITableDataSource) => void;
  handleEdit: (arg: string) => void;
  handleDelete: (arg: string) => void;
  renderStateFieldColor: (arg: ITableDataSource) => any;
  customFields?: ICustomFieldByObjectType[];
  type: string | null;
  id: string | undefined;
  showAction: boolean | null;
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
}

interface ILegalEntityGroupFirearmTableColumns {
  renderStateFieldColor: (arg: ITableDataSource) => any;
  id: string;
  groupId: string;
  type: string | null;
  hideManagement: boolean;
}

interface ILegalEntityGroupAncillaryTableColumns {
  id: string;
  groupId: string;
  type: string | null;
  ancillaryOpenDetail: string;
  setAncillaryOpenDetail: (arg: string) => void;
  hideManagement: boolean;
}

interface ILegalEntityAttachmentsTableColumns {
  t: (key: string) => string;
  showAction?: boolean;
  dateFormat: string;
  type?: string | null;
  handleView?: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDownload?: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    fileName: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDelete?: (attachmentUniqueId: string) => void;
  hasPermissionsAttachments: boolean;
  hasPermissionsLicence: boolean;
  isLicenceAuthorityValid: boolean;
  showLicenceColumns: boolean;
}

export const legalEntitiesTableColumns = (
  {
    handleEdit,
    handleDelete,
    handleSelect,
    type,
    customFields = [],
    t,
  }: ILegalEntityTableColumns,
  selectedLanguage: string | undefined,
  dateFormat: string,
  hasManageLEPermission: boolean,
  hasDeleteLEPermission: boolean,
  licenceAuthorityIds: string[],
): ITableColumns[] => {
  const customFieldColumns: ITableColumns[] = customFields
    .filter((customField) => customField.visibleInSearchResults)
    .map((customField) => ({
      id: customField.uniqueId,
      key: `customField.${customField.name}`,
      title: getColumnTitleFromCustomField(customField, selectedLanguage),
      isSortable: false,
      render: (data, props) => {
        if (props?.customFields?.length) {
          const cf = props.customFields.find(
            (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
          );
          if (cf) {
            if (cf.customFieldType === ECustomFieldType.BOOL && t) {
              if (cf.value === null) {
                return '-';
              }
              return cf.value
                ? t('customFields.value.yes.label')
                : t('customFields.value.no.label');
            }
            if (cf.customFieldType === ECustomFieldType.DATE) {
              return formatDate(cf.value, dateFormat);
            }
            return cf.value || '-';
          }
        }
        return '-';
      },
    }));

  return [
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
      key: 'displayUniqueId',
      title: 'legalEntities.individual.accordion.legalEntityTable.displayUniqueId.column',
      render: (_data, props) => (
        <span style={{ whiteSpace: 'nowrap' }}>{props.displayUniqueId}</span>
      ),
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
      key: 'firstName',
      title: 'legalEntities.individual.accordion.legalEntityTable.firstName.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f511',
      key: 'lastName',
      title: 'legalEntities.individual.accordion.legalEntityTable.lastName.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
      key: 'dateOfBirth',
      title: 'legalEntities.individual.accordion.legalEntityTable.dateBirth.column',
      dateFormat,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f518',
      key: 'city',
      title: 'legalEntities.individual.accordion.legalEntityTable.city.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f519',
      key: 'identityDocumentNumber',
      title:
        'legalEntities.individual.accordion.legalEntityTable.identityDocumentNumber.column',
    },
    ...customFieldColumns,
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
      key: '',
      title: 'legalEntities.individual.accordion.legalEntityTable.action.column',
      isSortable: false,
      width: '110px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <div>
          {type === ELEAccordionsPageTypes.LegalEntity ? (
            <>
              <Link
                to={`${ERouteLinks.ViewLegalEntity.replace(':id', props.id)}?type=${
                  ELegalEntityTypes.individual
                }`}
              >
                <Button
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.view}
                  id={`action_view_${props.id}`}
                />
              </Link>
              {hasManageLEPermission &&
                (!props.primaryWeaponLicencingAuthorityUniqueId ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  ) ||
                  licenceAuthorityIds.includes(
                    props.secondaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.edit}
                    onClick={() => handleEdit && handleEdit(props.id)}
                    id={`action_edit_${props.id}`}
                  />
                )}
              {hasDeleteLEPermission &&
                (!props.primaryWeaponLicencingAuthorityUniqueId ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  ) ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.delete}
                    onClick={() => handleDelete && handleDelete(props.id)}
                    permission={[EPermission.DELETE_LE]}
                    id={`action_delete_${props.id}`}
                  />
                )}
            </>
          ) : (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.checkmark}
              id={`ind_action_check_${props.id}`}
              onClick={() =>
                handleSelect &&
                handleSelect(
                  type === ELEAccordionsPageTypes.SelectLegalEntity ? props : props.id,
                )
              }
            />
          )}
        </div>
      ),
    },
  ];
};

export const legalEntityFirearmsTableColumns = ({
  renderStateFieldColor,
  type,
  showAction,
  t,
  hasWeaponLicenceModule,
}: ILegalEntityFirearmTableColumns): ITableColumns[] => {
  const infoColumn: ITableColumns[] = [
    {
      id: '4eaea4ee-d36f-400b-895b-231c81f091cf',
      key: 'info',
      width: '58px',
      isSortable: false,
      title: 'legalEntityDetails.firearm.accordion.info.column',
      render: (_data, props) => {
        const textToTooltip = [];
        if (hasWeaponLicenceModule) {
          if (props.showNoActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoActiveLicenceWarning.text'));
          }
          if (props.showNoOwnerActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoOwnerActiveLicenceWarning.text'));
          }
          if (props.showNoKeeperActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoKeeperActiveLicenceWarning.text'));
          }
          if (props.showExpiredLinkedWarning) {
            textToTooltip.push(t('registeredFirearms.showExpiredLinkedWarning.text'));
          }
        }
        if (props.showInactiveOwnerOrKeeperWarning) {
          textToTooltip.push(t('registeredFirearms.showInactiveOwnerOrKeeperWarning.text'));
        }

        return (
          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
            {!!textToTooltip.length && (
              <Tooltip titleArray={textToTooltip}>
                <Icon type={EIconTypes.infoWarning} fontSize={20} />
              </Tooltip>
            )}
            {props.groupName && (
              <Tooltip
                title={t('registeredFirearms.groupName.text').replace(
                  ':name',
                  props.groupName,
                )}
              >
                <Icon type={EIconTypes.group} fontSize={20} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
  return [
    ...infoColumn,
    {
      id: 'f0504ca-6b32-482d-b369-304b6f32f510',
      key: 'fireCycleId',
      title: 'legalEntityDetails.firearm.accordion.firecycleId.column',
    },
    {
      id: 'f0504ca-6b32-482d-b369-304a6f32f510',
      key: 'serialNumber',
      title: 'legalEntityDetails.firearm.accordion.serialNumber.column',
    },
    {
      id: 'h0504ca6-6b32-482d-b369-304a6f32f411',
      key: 'exhibitNumber',
      title: 'legalEntityDetails.firearm.accordion.exhibitNumber.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f511',
      key: 'make',
      title: 'legalEntityDetails.firearm.accordion.make.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f512',
      key: 'model',
      title: 'legalEntityDetails.firearm.accordion.model.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f513',
      key: 'manufacturer',
      title: 'legalEntityDetails.firearm.accordion.manufacturer.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f514',
      key: 'caliber',
      title: 'legalEntityDetails.firearm.accordion.calibre.column',
    },
    {
      id: 'h6404ca6-6b32-482d-b369-304a6f32f515',
      key: 'owner',
      title: 'legalEntityDetails.firearm.accordion.owner.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f515',
      key: 'keeper',
      title: 'legalEntityDetails.firearm.accordion.keeper.column',
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f516',
      key: 'state',
      title: 'legalEntityDetails.firearm.accordion.state.column',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <Chips text={props.state} variant={renderStateFieldColor(props)} />
      ),
    },
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f517',
      key: '',
      title: 'groups.accordion.action.column',
      isSortable: false,
      align: 'center',
      isCenterTableHead: true,
      width: '60px',
      render: (_data, props) =>
        showAction ? (
          <Link
            to={`${ERouteLinks.ViewRegisteredFirearm.replace(':id', props.id)}?type=${type}`}
          >
            <Button
              id={`firearm_view_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.view}
            />
          </Link>
        ) : (
          ''
        ),
    },
  ];
};

export const legalEntityAncillariesTableColumns = ({
  renderStateFieldColor,
  showDetail,
  t,
}: ILegalEntityAncillaryTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca-6b32-482d-b369-304a6f32f509',
    key: 'info',
    title: 'legalEntityDetails.ancillary.accordion.info.column',
    isSortable: false,
    align: 'center',
    width: '58px',
    render: (_data, props) => (
      <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
        {props.showInactiveOwnerOrKeeperWarning && (
          <Tooltip titleArray={[t('ancillaries.showInactiveOwnerOrKeeperWarning.text')]}>
            <Icon type={EIconTypes.infoWarning} fontSize={20} />
          </Tooltip>
        )}
        {props.groupName && (
          <Tooltip title={props.groupName}>
            <Icon type={EIconTypes.group} fontSize={20} />
          </Tooltip>
        )}
      </div>
    ),
  },
  {
    id: 'f0504ca-6b32-482d-b369-304b6f32f510',
    key: 'fireCycleId',
    title: 'legalEntityDetails.ancillary.accordion.firecycleId.column',
  },
  {
    id: 'f0504ca-6b32-482d-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'legalEntityDetails.ancillary.accordion.serialNumber.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f511',
    key: 'ancillaryDictionaryValue',
    title: 'legalEntityDetails.ancillary.accordion.ancillaryDictionaryValue.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f512',
    key: 'ancillaryType',
    title: 'legalEntityDetails.ancillary.accordion.ancillaryType.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f513',
    key: 'manufacturer',
    title: 'legalEntityDetails.ancillary.accordion.manufacturer.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f514',
    key: 'calibre',
    title: 'legalEntityDetails.ancillary.accordion.calibre.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f515',
    key: 'keeperName',
    title: 'legalEntityDetails.ancillary.accordion.keeper.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f516',
    key: 'state',
    title: 'legalEntityDetails.ancillary.accordion.state.column',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} />
    ),
  },
  {
    id: '171f9c58-9f45-11ee-8c90-0242ac120002',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    align: 'center',
    isCenterTableHead: true,
    width: '60px',
    render: (_data, props) => {
      if (showDetail) {
        return (
          <Link
            to={ERouteLinks.ViewAncillary.replace(':id', props.id as string).replace(
              ':type',
              getAncillaryType(props.ancillaryDictionaryUniqueId),
            )}
          >
            <Button
              id={`ancillary_view_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.view}
            />
          </Link>
        );
      }
      return '';
    },
  },
];

export const legalEntityAncillaryGroupsTableColumns = ({
  handleAddToGroup,
  handleEdit,
  handleDelete,
  renderStateFieldColor,
  type,
  id,
  showAction,
  isAnyLicenceAuthorityValid,
}: ILegalEntityAncillaryGroupTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca-6b32-482d-b369-304a6f32f509',
    key: 'name',
    title: 'legalEntityDetails.ancillaryGroup.accordion.groupName.column',
  },
  {
    id: 'f0504ca-6b32-482d-b369-304a6f32f510',
    key: 'ancillaryAmount',
    title: 'legalEntityDetails.ancillaryGroup.accordion.ancillaryCount.column',
  },
  {
    id: '171f9a14-9f45-11ee-8c90-0242ac120002',
    key: 'owner',
    title: 'legalEntityDetails.ancillaryGroup.accordion.owner.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f515',
    key: 'keeper',
    title: 'legalEntityDetails.ancillaryGroup.accordion.keeper.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f516',
    key: 'state',
    title: 'legalEntityDetails.ancillaryGroup.accordion.state.column',
    isCenterTableHead: true,
    align: 'center',
    render: (_data, props) =>
      props.state ? <Chips text={props.state} variant={renderStateFieldColor(props)} /> : '-',
  },
  {
    id: '171f9c58-9f45-11ee-8c90-0242ac120002',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    isCenterTableHead: true,
    minWidth: '56px',
    width: showAction ? '130px' : 'auto',
    align: 'center',
    render: (_data, props) => {
      if (showAction && isAnyLicenceAuthorityValid(props.licenceAuthorityIds)) {
        return (
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid size={{ xs: 3 }}>
              <Button
                id={`ancillary_group_add_button-${props.id}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.plus}
                onClick={() => handleAddToGroup(props)}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Link
                to={`${ERouteLinks.ViewLegalEntityAncillaryGroup.replace(
                  ':id',
                  id as string,
                ).replace(':groupId', props.id)}?type=${type}&expand=ancillaryGroups`}
              >
                <Button
                  id={`ancillary_group_view_button-${props.id}`}
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.view}
                />
              </Link>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Button
                id={`ancillary_group_edit_button-${props.id}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.edit}
                onClick={() => handleEdit(props.id)}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Button
                id={`ancillary_group_delete_button-${props.id}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.delete}
                onClick={() => handleDelete(props.id)}
              />
            </Grid>
          </Grid>
        );
      }
      return (
        <Link
          to={`${ERouteLinks.ViewLegalEntityAncillaryGroup.replace(
            ':id',
            id as string,
          ).replace(':groupId', props.id)}?type=${type}&expand=ancillaryGroups`}
        >
          <Button
            id={`ancillary_group_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
          />
        </Link>
      );
    },
  },
];

export const legalEntityGroupFirearmsTableColumns = ({
  renderStateFieldColor,
  id,
  groupId,
  type = '',
  hideManagement,
}: ILegalEntityGroupFirearmTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca-6b32-4821-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'groupDetails.firearmListSection.serialNumber.column',
  },
  {
    id: 'f0504ca6-6b32-4822-b369-304a6f32f511',
    key: 'make',
    title: 'groupDetails.firearmListSection.make.column',
  },
  {
    id: 'f0504ca6-6b32-4823-b369-304a6f32f512',
    key: 'model',
    title: 'groupDetails.firearmListSection.model.column',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
    key: 'caliber',
    title: 'groupDetails.firearmListSection.calibre.column',
  },
  {
    id: 'f0504ca6-6b32-4827-b369-304a6f32f516',
    key: 'state',
    title: 'groupDetails.firearmListSection.state.column',
    align: 'center',
    isCenterTableHead: true,
    isSortable: false,
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504ca6-6b32-4828-b369-304a6f32f517',
    key: '',
    title: 'groupDetails.firearmListSection.action.column',
    isSortable: false,
    isCenterTableHead: true,
    align: 'center',
    width: '80px',
    render: (_data, props) => (
      <Grid sx={{ justifyContent: 'center' }}>
        <Link to={ERouteLinks.ViewRegisteredFirearm.replace(':id', props.id)}>
          <Button
            id={`firearm_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            permission={[EPermission.VIEW_FIREARMS]}
          />
        </Link>
        {!hideManagement && (
          <Link
            to={`${ERouteLinks.LegalEntityGroupDeleteFirearm.replace(':id', id)
              .replace(':groupId', groupId)
              .replace(':firearmId', props.id)}?type=${type}&expand=groups`}
          >
            <Button
              id={`firearm_delete_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              permission={[EPermission.MANAGE_GROUPS_LE]}
            />
          </Link>
        )}
      </Grid>
    ),
  },
];
export const legalEntityGroupsTableColumns = ({
  handleAdd,
  handleEdit,
  handleDelete,
  type,
  renderStateFieldColor,
  id,
  showAction,
  isAnyLicenceAuthorityValid,
}: ILegalEntityGroupTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca-6b33-482d-b369-304a6f32f510',
    key: 'name',
    title: 'groups.accordion.groupName.column',
  },
  {
    id: 'f0504ca6-6b33-482d-b369-304a6f32f515',
    key: 'firearmAmount',
    title: 'groups.accordion.firearmCount.column',
  },
  {
    id: 'f0504ca6-6b33-482d-b369-304a6f32f516',
    key: 'owner',
    title: 'groups.accordion.owner.column',
  },
  {
    id: 'f0504ca6-6b33-482d-b369-304a6f32f517',
    key: 'keeper',
    title: 'groups.accordion.keeper.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f518',
    key: 'state',
    title: 'groups.accordion.state.column',
    isCenterTableHead: true,
    align: 'center',
    render: (data, props) => {
      if (!data) {
        return '-';
      }
      return <Chips text={props.state} variant={renderStateFieldColor(props)} />;
    },
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f519',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    width: '130px',
    isCenterTableHead: true,
    align: 'center',
    render: (_data, props) => (
      <>
        {showAction && isAnyLicenceAuthorityValid(props.licenceAuthorityIds) && (
          <Button
            id={`legal_entity_group_add_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.plus}
            onClick={() => handleAdd(props)}
          />
        )}
        <Link
          to={`${ERouteLinks.ViewLegalEntityGroup.replace(':id', id as string).replace(
            ':groupId',
            props.id,
          )}?type=${type}&expand=groups`}
        >
          <Button
            id={`legal_entity_group_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
          />
        </Link>
        {showAction && isAnyLicenceAuthorityValid(props.licenceAuthorityIds) && (
          <>
            <Button
              id={`legal_entity_group_edit_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.edit}
              onClick={() => handleEdit(props.id)}
            />
            <Button
              id={`legal_entity_group_delete_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.id)}
            />
          </>
        )}
      </>
    ),
  },
];

export const mapLEToDataSource = (
  items: ILegalEntity[],
  visibility?: IParsedDictionary,
  gender?: IParsedDictionary,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    firstName: item.firstName,
    lastName: item.lastName,
    dateOfBirth: item.dateOfBirth,
    city: item.city ?? '-',
    displayUniqueId: item.displayUniqueId ?? '-',
    customFields: item.customFields || [],
    visibility:
      item.visibilityUniqueId && visibility?.[item.visibilityUniqueId]
        ? visibility[item.visibilityUniqueId]
        : '',
    gender:
      item.genderUniqueId && gender?.[item.genderUniqueId] ? gender[item.genderUniqueId] : '',
    identityDocumentNumber: item.identityDocumentNumber ?? '-',
    primaryWeaponLicencingAuthorityUniqueId: item.primaryWeaponLicencingAuthorityUniqueId,
    secondaryWeaponLicencingAuthorityUniqueId: item.secondaryWeaponLicencingAuthorityUniqueId,
    prefixGroupUniqueId: item.prefixGroupUniqueId,
  }));

export const mapLEFirearmToDataSource = (
  items: ILegalEntityFirearm[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  manufacturer: IDictionary[] | null,
  caliber: IDictionary[] | null,
  state: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    serialNumber: item.serialNumber,
    make: findInDictionary(item.makeUniqueId, make),
    model: findInDictionary(item.modelUniqueId, model),
    manufacturer: findInDictionary(item.manufacturerUniqueId, manufacturer),
    caliber: findInDictionary(item.caliberUniqueId, caliber),
    keeper: item.keeper,
    stateUniqueId: item.stateUniqueId,
    state: findInDictionary(item.stateUniqueId, state),
    groupName: item.groupName,
    fireCycleId: item.fireCycleId,
    activityUniqueId: item.activityUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    owner: item.owner,
    exhibitNumber: item.exhibitNumber,
    showNoActiveLicenceWarning: item.showNoActiveLicenceWarning,
    showNoOwnerActiveLicenceWarning: item.showNoOwnerActiveLicenceWarning,
    showNoKeeperActiveLicenceWarning: item.showNoKeeperActiveLicenceWarning,
    showExpiredLinkedWarning: item.showExpiredLinkedWarning,
    showInactiveOwnerOrKeeperWarning: item.showInactiveOwnerOrKeeperWarning,
  }));

export const mapLEAncillaryToDataSource = (
  items: ILegalEntityAncillaries[],
  state: IDictionary[] | null,
  manufacturer: IDictionary[] | null,
  caliber: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    activityUniqueId: item.activityUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    serialNumber: item.serialNumber,
    stateUniqueId: item.stateUniqueId,
    state: findInDictionary(item.stateUniqueId, state),
    manufacturer: findInDictionary(item.manufacturerUniqueId, manufacturer),
    keeperId: item.keeperId,
    keeperName: item.keeperName,
    ancillaryDictionaryValue: item.ancillaryDictionaryValue,
    ancillaryDictionaryUniqueId: item.ancillaryDictionaryUniqueId,
    calibre: findInDictionary(item.calibreUniqueId, caliber),
    ancillaryType: item.ancillaryType,
    fireCycleId: item.fireCycleId,
    groupName: item.groupName,
    showInactiveOwnerOrKeeperWarning: item.showInactiveOwnerOrKeeperWarning,
  }));

export const mapLEAncillaryGropsToDataSource = (
  items: ILegalEntityAncillaryGroup[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    ancillaryAmount: item.ancillaryAmount,
    state: item.state,
    owner: item.owner,
    keeper: item.keeper,
    legalityUniqueId: item.legalityUniqueId,
    activityUniqueId: item.activityUniqueId,
    stateUniqueId: item.stateUniqueId,
    hasDifferentStates: item.hasDifferentStates,
    licenceAuthorityIds: item.licenceAuthorityIds,
  }));

export const AncillaryTableColumns = ({
  callback,
  selectAllCallback,
  areAllAncillariesSelected,
  selectedAncillaries,
  selectedAncillaryType,
  renderStateFieldColor,
}: IAncillariesTableColumns): ITableColumns[] => [
  {
    id: '0504ca-6b32-482d-b369-304a6f32f5220',
    key: 'isSelected',
    align: 'center',
    title: () => (
      <StyledCheckboxBackgroundWrapper backgroundColor={colors.white}>
        <Checkbox
          id="select-all-checkbox"
          sx={{
            color: colors.gray400,
            '&.Mui-checked': {
              color: defaultCheckboxStyle.backgroundColorChecked,
            },
            '&.Mui-disabled': {
              color: defaultCheckboxStyle.backgroundColorDisabled,
            },
          }}
          checked={areAllAncillariesSelected}
          onChange={() => selectAllCallback()}
        />
      </StyledCheckboxBackgroundWrapper>
    ),
    isSortable: false,
    width: '20px',
    render: (_data, props) => (
      <Checkbox
        id="select-checkbox"
        sx={{
          color: colors.gray300,
          '&.Mui-checked': {
            color: colors.primary500,
          },
          '&.MuiCheckbox-root': {
            maxWidth: '16px',
            maxHeight: '16px',
          },
        }}
        checked={
          !!selectedAncillaries.filter((item) => item.uniqueId === props.uniqueId).length
        }
        onChange={() => callback(props)}
      />
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'fireCycleId',
    title: 'searchForAncillary.list.fireCycleId.column',
    isSortable: false,
  },
  ...(selectedAncillaryType === EArtAncillaryType.AMMUNITION
    ? [
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
          key: 'lotNumber',
          title: 'searchForAncillary.list.lotNumber.column',
          isSortable: false,
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
          key: 'batchNumber',
          title: 'searchForAncillary.list.batchNumber.column',
          isSortable: false,
        },
      ]
    : [
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
          key: 'serialNumber',
          title: 'searchForAncillary.list.serialNumber.column',
          isSortable: false,
        },
      ]),
  {
    id: '7827a5d0-9807-11ee-b9d1-0242ac120002',
    key: 'ancillaryTypeDictionaryValue',
    title: 'searchForAncillary.list.ancillary.column',
    isSortable: false,
  },
  {
    id: '7827a5d0-9807-11ee-b9d1-0242ac1200022',
    key: 'ancillarySpecificType',
    title: 'searchForAncillary.list.ancillaryType.column',
    isSortable: false,
  },
  {
    id: 'd523d924-470b-4331-816c-c4d18b12219d',
    key: 'owner',
    title: 'searchForAncillary.list.owner.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: 'keeper',
    title: 'searchForAncillary.list.keeper.column',
    isSortable: false,
  },
  {
    id: '7827a85a-9807-11ee-b9d1-0242ac120002',
    key: 'calibre',
    title: 'searchForAncillary.list.calibre.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
    key: 'state',
    title: 'searchForAncillary.list.state.column',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} />
    ),
    isSortable: false,
  },
  {
    id: '7827a85a-9807-aaac-b9d1-0242ac120002',
    key: 'address',
    title: 'searchForAncillary.list.address.column',
    isSortable: false,
  },
  {
    id: '7827a85a-9807-11ee-b9d1-0242ac120002',
    key: 'gps',
    title: 'searchForAncillary.list.gps.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    width: '80px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Box sx={{ textAlign: 'center', display: 'flex' }}>
        <Link
          to={ERouteLinks.ViewAncillary.replace(':id', props.id).replace(
            ':type',
            props.ancillaryType.toLowerCase(),
          )}
          target="_blank"
        >
          <Button
            id={`ancillary_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            permission={[EPermission.VIEW_LIMITED_ANCILLARIES]}
          />
        </Link>
        {selectedAncillaries.filter((item) => item.uniqueId === props.id).length ? (
          <Button
            id={`ancillary_clear_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.clear}
            onClick={() => callback(props)}
          />
        ) : (
          <Button
            id={`ancillary_select_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.checkmark}
            onClick={() => callback(props)}
          />
        )}
      </Box>
    ),
  },
];

export const mapAncillaryGroupAncillariesToDataSource = (
  ancillaries: IAncillary[],
): ITableDataSource[] =>
  ancillaries.map((item: any) => ({
    id: item.uniqueId,
    uniqueId: item.uniqueId,
    fireCycleId: item.fireCycleId,
    lotNumber: item.lotNumber,
    batchNumber: item.batchNumber,
    serialNumber: item.serialNumber,
    manufacturer: item.manufacturer,
    ancillaryTypeDictionaryValue: item.ancillaryTypeDictionaryValue,
    ancillarySpecificType: item.ancillarySpecificType,
    ancillaryType: item.ancillaryType,
    calibre: item.calibre,
    state: item.state,
    stateUniqueId: item.stateUniqueId,
    owner: item.owner,
    keeper: item.keeper,
    ownerUniqueId: item.ownerUniqueId,
    keeperUniqueId: item.keeperUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    activityUniqueId: item.activityUniqueId,
    latitude: item.latitude,
    longitude: item.longitude,
    address: item.address,
    gps: formatGPSField(item.latitude, item.longitude),
  }));

export const mapLEGFirearmToDataSource = (
  items: ILegalEntityFirearm[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  caliber: IDictionary[] | null,
  state: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    serialNumber: item.serialNumber,
    make: findInDictionary(item.makeUniqueId, make),
    model: findInDictionary(item.modelUniqueId, model),
    caliber: findInDictionary(item.caliberUniqueId, caliber),
    stateUniqueId: item.stateUniqueId,
    state: findInDictionary(item.stateUniqueId, state),
    activityUniqueId: item.activityUniqueId,
    legalityUniqueId: item.legalityUniqueId,
  }));

export const mapLEGroupToDataSource = (items: ILegalEntityGroup[]): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    numberOfFirearm: item.firearmAmount,
    firearmAmount: item.firearmAmount,
    owner: item.owner,
    keeper: item.keeper,
    state: item.state,
    activityUniqueId: item.activityUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    stateUniqueId: item.stateUniqueId,
    hasDifferentStates: item.hasDifferentStates,
    licenceAuthorityIds: item.licenceAuthorityIds,
    gps: formatGPSField(item.latitude, item.longitude),
  }));

export const organizationLegalEntityTableColumns = (
  {
    handleEdit,
    handleDelete,
    handleSelect,
    type,
    customFields = [],
    t,
  }: ILegalEntityTableColumns,
  selectedLanguage: string | undefined,
  dateFormat: string,
  hasDeleteLEPermission: boolean,
  hasManageLEPermission: boolean,
  licenceAuthorityIds: string[],
): ITableColumns[] => {
  const customFieldColumns: ITableColumns[] = customFields
    .filter((customField) => customField.visibleInSearchResults)
    .map((customField) => ({
      id: customField.uniqueId,
      key: `customField.${customField.name}`,
      title: getColumnTitleFromCustomField(customField, selectedLanguage),
      isSortable: false,
      render: (data, props) => {
        if (props?.customFields?.length) {
          const cf = props.customFields.find(
            (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
          );
          if (cf) {
            if (cf.customFieldType === ECustomFieldType.BOOL && t) {
              if (cf.value === null) {
                return '-';
              }
              return cf.value
                ? t('customFields.value.yes.label')
                : t('customFields.value.no.label');
            }
            if (cf.customFieldType === ECustomFieldType.DATE) {
              return formatDate(cf.value, dateFormat);
            }
            return cf.value || '-';
          }
        }
        return '-';
      },
    }));

  return [
    {
      id: 'f0504ca5-6b32-482d-b369-304a9f32f585',
      key: 'displayUniqueId',
      title: 'legalEntities.organization.accordion.legalEntityTable.displayUniqueId.column',
      width: '180px',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
      key: 'organizationName',
      title: 'legalEntities.organization.accordion.legalEntityTable.name.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a9f32f515',
      key: 'organisationCategory',
      render: (_data, props) => props.organisationCategoryUniqueId,
      title:
        'legalEntities.organization.accordion.legalEntityTable.organisationCategoryUniqueId.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
      key: 'organizationType',
      isSortable: false,
      render: (_data, props) => props.organizationTypeUniqueId,
      title: 'legalEntities.organization.accordion.legalEntityTable.organizationType.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f518',
      key: 'departmentName',
      title: 'legalEntities.organization.accordion.legalEntityTable.departmentName.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f519',
      key: 'city',
      title: 'legalEntities.organization.accordion.legalEntityTable.city.column',
    },
    ...customFieldColumns,
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f521',
      key: '',
      title: 'legalEntities.organization.accordion.legalEntityTable.action.column',
      isSortable: false,
      width: '110px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <div>
          {type === ELEAccordionsPageTypes.LegalEntity ? (
            <>
              <Link
                to={`${ERouteLinks.ViewLegalEntity.replace(':id', props.id)}?type=${
                  ELegalEntityTypes.organisation
                }`}
              >
                <Button
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.view}
                  id={`action_view_${props.id}`}
                />
              </Link>
              {hasManageLEPermission &&
                (!props.primaryWeaponLicencingAuthorityUniqueId ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  ) ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.edit}
                    onClick={() => handleEdit && handleEdit(props.id)}
                    id={`action_edit_${props.id}`}
                  />
                )}
              {hasDeleteLEPermission &&
                (!props.primaryWeaponLicencingAuthorityUniqueId ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  ) ||
                  licenceAuthorityIds.includes(
                    props.primaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.delete}
                    onClick={() => handleDelete && handleDelete(props.id)}
                    permission={[EPermission.DELETE_LE]}
                    id={`action_delete_${props.id}`}
                  />
                )}
            </>
          ) : (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.checkmark}
              id={`org_action_check_${props.id}`}
              onClick={() =>
                handleSelect &&
                handleSelect(
                  type === ELEAccordionsPageTypes.SelectLegalEntity ? props : props.id,
                )
              }
            />
          )}
        </div>
      ),
    },
  ];
};

const getOrganisationType = (
  organisationTypeUniqueIds: string[],
  organisationTypes: string[],
  t: (key: string) => string,
) => {
  if (organisationTypeUniqueIds.length === 1) return organisationTypes[0];

  if (!organisationTypeUniqueIds.length) return '-';

  return t('general.multiple.label');
};

export const mapOLEToDataSource = (
  items: IOrganizationLegalEntity[],
  t: (key: string) => string,
  organizationType: IParsedDictionary,
  organisationCategory: IParsedDictionary,
  visibility?: IParsedDictionary,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    organizationName: item.organizationName,
    organizationTypeUniqueId: getOrganisationType(
      item.organisationTypeUniqueIds,
      item.organisationTypes,
      t,
    ),
    organisationCategoryUniqueId:
      item.organisationCategoryUniqueId &&
      organisationCategory?.[item.organisationCategoryUniqueId]
        ? organisationCategory[item.organisationCategoryUniqueId]
        : '',
    departmentName: item.departmentName,
    city: item.city || '-',
    displayUniqueId: item.displayUniqueId || '-',
    customFields: item.customFields || [],
    visibility:
      item.visibilityUniqueId && visibility?.[item.visibilityUniqueId]
        ? visibility[item.visibilityUniqueId]
        : '',
    primaryWeaponLicencingAuthorityUniqueId: item.primaryWeaponLicencingAuthorityUniqueId,
    secondaryWeaponLicencingAuthorityUniqueId: item.secondaryWeaponLicencingAuthorityUniqueId,
  }));

interface ILegalEntityAddressTableColumns {
  handleEdit: (arg: string) => void;
  handleShowOnMap: (arg: string) => void;
  handleDelete: (arg: string) => void;
  t: (key: string) => string;
  licenceAuthorityValid?: boolean;
}

const firearmAmountDisplayMap = {
  [EAddressAssetAmount.NONE]: { icon: undefined, text: '' },
  [EAddressAssetAmount.SINGLE]: {
    icon: <Icon type={EIconTypes.singleAsset} fontSize={24} />,
    text: 'general.single.firearm.text',
  },
  [EAddressAssetAmount.MULTIPLE]: {
    icon: <Icon type={EIconTypes.multipleAssets} fontSize={24} />,
    text: 'general.multiple.firearm.text',
  },
};

const ancillaryAmountDisplayMap = {
  [EAddressAssetAmount.NONE]: { icon: undefined, text: '' },
  [EAddressAssetAmount.SINGLE]: {
    icon: <Icon type={EIconTypes.singleAncillaryAsset} fontSize={24} />,
    text: 'general.single.ancillary.text',
  },
  [EAddressAssetAmount.MULTIPLE]: {
    icon: <Icon type={EIconTypes.multipleAncillaryAssets} fontSize={24} />,
    text: 'general.multiple.ancillary.text',
  },
};

const tooltipText = (data: { icon: any; text: string }, t: (key: string) => string) => {
  const { icon, text } = data;
  if (!icon) {
    return '';
  }
  return (
    <Box
      sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Tooltip title={t(text)}>{icon}</Tooltip>
    </Box>
  );
};

export const legalEntityAddressTableColumns = ({
  handleEdit,
  handleShowOnMap,
  handleDelete,
  t,
  licenceAuthorityValid = true,
}: ILegalEntityAddressTableColumns): ITableColumns[] => [
  {
    id: 'f0504bb2-6b32-482d-b369-304a6f32f500',
    key: 'addressAssetAmount',
    title: 'legalEntityDetails.addressTab.info.column',
    isCenterTableHead: true,
    align: 'center',
    isSortable: false,
    width: '75px',
    render: (_data, props) => (
      <Grid
        sx={{
          display: 'flex',
          mb: '5px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        {tooltipText(firearmAmountDisplayMap[props.firearmAmount as EAddressAssetAmount], t)}
        {tooltipText(
          ancillaryAmountDisplayMap[props.ancillaryAmount as EAddressAssetAmount],
          t,
        )}
      </Grid>
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f500',
    key: 'storage',
    title: 'legalEntityDetails.addressTab.storageAddress.column',
    isCenterTableHead: true,
    align: 'center',
    width: '160px',
    render: (_data, props) => (
      <Chips
        text={
          props.storage
            ? t('general.confirmation.yes.button')
            : t('general.confirmation.no.button')
        }
        fullWidth
        variant={props.storage ? EChipsVariants.success : EChipsVariants.inactive}
      />
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'typeUniqueId',
    title: 'legalEntityDetails.addressTab.addressType.column',
    align: 'center',
    isCenterTableHead: true,
    width: '160px',
    render: (_data, props) =>
      props.typeUniqueId ? (
        <Chips text={props.typeUniqueId} variant={EChipsVariants.primary} fullWidth />
      ) : (
        '-'
      ),
  },

  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'line1',
    title: 'legalEntityDetails.addressTab.address.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'city',
    title: 'legalEntityDetails.addressTab.city.column',
  },
  {
    id: '67bdb46f-25b8-4f2c-87da-293dead90d3f',
    key: 'regionUniqueId',
    title: 'legalEntityDetails.addressTab.stateProvince.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: 'countryUniqueId',
    title: 'legalEntityDetails.addressTab.country.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
    key: 'gps',
    title: 'legalEntityDetails.addressTab.gps.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
    key: 'postalCode',
    title: 'legalEntityDetails.addressTab.zipPostalCode.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f519',
    key: '',
    title: 'legalEntityDetails.addressTab.action.column',
    isSortable: false,
    align: 'center',
    width: '120px',
    isCenterTableHead: true,
    render: (_data, props) => {
      if (!licenceAuthorityValid) {
        if (
          (props.ancillaryAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE ||
          (props.firearmAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE
        ) {
          return (
            <Button
              id={`legal_entity_details_address-show-on-map-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.map}
              onClick={() => handleShowOnMap(props.id)}
            />
          );
        }

        return null;
      }

      return (
        licenceAuthorityValid && (
          <>
            <Button
              id={`legal_entity_details_address-edit-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.edit}
              onClick={() => handleEdit(props.id)}
              permission={[EPermission.MANAGE_LE]}
            />
            {((props.ancillaryAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE ||
              (props.firearmAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE) && (
              <Button
                id={`legal_entity_details_address-show-on-map-${props.id}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.map}
                onClick={() => handleShowOnMap(props.id)}
              />
            )}
            <Button
              id={`legal_entity_details_address-delete-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.id)}
              permission={[EPermission.DELETE_LE]}
            />
          </>
        )
      );
    },
  },
];

export const mapLEAddressToDataSource = (
  items: ILegalEntityExtendedAddress[],
  region: IParsedDictionary,
  country: IParsedDictionary,
  type: IParsedDictionary,
  legalEntityId: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    typeUniqueId:
      item.typeUniqueId && type?.[item.typeUniqueId] ? type[item.typeUniqueId] : '',
    line1: `${item.line1} ${item.line2}`,
    city: item.city,
    postalCode: item.postalCode,
    regionUniqueId:
      item.regionUniqueId && region?.[item.regionUniqueId] ? region[item.regionUniqueId] : '',
    countryUniqueId:
      item.countryUniqueId && country?.[item.countryUniqueId]
        ? country[item.countryUniqueId]
        : '',
    gps: formatGPSField(item.latitude, item.longitude),
    latitude: item.latitude,
    longitude: item.longitude,
    isDefault: item.isDefault,
    storage: item.storage,
    firearmAmount: item.firearmAmount,
    ancillaryAmount: item.ancillaryAmount,
    legalEntityId,
  }));

interface ILegalEntityContactTableColumns {
  handleEdit: (arg: string) => void;
  handleDelete: (arg: string) => void;
  type: ELegalEntityTypes;
  account?: IAccountResponse;
  showActions?: boolean;
  hasPermissions: (
    permissions: EPermission | EPermission[],
    isORCondition?: boolean,
  ) => boolean | null;
}

export const legalEntityContactTableColumns = ({
  handleEdit,
  handleDelete,
  type,
  account,
  hasPermissions,
  showActions = true,
}: ILegalEntityContactTableColumns): ITableColumns[] =>
  type === ELegalEntityTypes.organisation
    ? [
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
          key: 'name',
          title: 'legalEntityDetails.contactTab.contactName.column',
          render: (_data, props) => {
            if (props.assignedIndividualUniqueId && props.canEdit) {
              return (
                <StyledLegalLink
                  labelText={props.name}
                  legalType={ELegalEntityTypes.individual}
                  legalId={props.assignedIndividualUniqueId}
                  legalVisibilityUniqueId={props.assignedIndividualVisibilityUniqueId}
                />
              );
            }
            return <Typography variant="body2">{props.name}</Typography>;
          },
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
          key: 'role',
          title: 'legalEntityDetails.contactTab.role.column',
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
          key: 'phoneNumber',
          title: 'legalEntityDetails.contactTab.phoneNumber.column',
        },
        {
          id: '422566d7-a211-483b-b2dc-fd2bc12e2a58',
          key: 'email',
          title: 'legalEntityDetails.contactTab.emailAddress.column',
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
          key: '',
          title: 'legalEntityDetails.contactTab.action.column',
          isSortable: false,
          isCenterTableHead: true,
          width: '80px',
          align: 'center',
          render: (_data, props) =>
            showActions && (
              <>
                {props.canEdit && (
                  <Button
                    id={`legal_entity_details_contact-edit-${props.id}`}
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.edit}
                    onClick={() => handleEdit(props.id)}
                    permission={[EPermission.MANAGE_LE]}
                  />
                )}
                <Button
                  id={`legal_entity_details_contact-delete-${props.id}`}
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.delete}
                  onClick={() => handleDelete(props.id)}
                  permission={[EPermission.DELETE_LE]}
                />
              </>
            ),
        },
      ]
    : [
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f538',
          key: 'legalContactType',
          title: 'legalEntityDetails.contactTab.contactType.column',
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
          key: 'phoneNumber',
          title: 'legalEntityDetails.contactTab.phoneNumber.column',
        },
        {
          id: '4457e074-e590-42ee-a30a-7cc50710e95d',
          key: 'email',
          title: 'legalEntityDetails.contactTab.emailAddress.column',
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f537',
          key: 'assignedOrganisationName',
          title: 'legalEntityDetails.contactTab.associatedOrganisation.column',
          render: (_data, props) => {
            if (props.assignedOrganisationVisibilityUniqueId) {
              return (
                <StyledLegalLink
                  labelText={props.assignedOrganisationName}
                  legalType={ELegalEntityTypes.organisation}
                  legalId={props.assignedOrganisationUniqueId}
                  legalVisibilityUniqueId={props.assignedOrganisationVisibilityUniqueId}
                  legalOrganisationCategoryUniqueId={
                    props.assignedOrganisationCategoryUniqueId
                  }
                />
              );
            }
            return <Typography variant="body2">{emptyFieldHolder(props.name)}</Typography>;
          },
        },
        {
          id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
          key: '',
          title: 'legalEntityDetails.contactTab.action.column',
          isSortable: false,
          isCenterTableHead: true,
          width: '80px',
          align: 'center',
          render: (_data, props) =>
            showActions &&
            (!props.assignedOrganisationUniqueId ||
              (account &&
                account.organisationCategoryIds.includes(
                  props.assignedOrganisationCategoryUniqueId,
                ) &&
                (hasPermissions(EPermission.VIEW_ALL_LE) ||
                  (hasPermissions(EPermission.VIEW_LE) &&
                    (props.assignedOrganisationVisibilityUniqueId ===
                      ELegalVisibility.PUBLIC ||
                      account.assignedLegalIds.includes(
                        props.assignedOrganisationUniqueId,
                      )))))) && (
              <>
                {props.canEdit && (
                  <Button
                    id={`legal_entity_details_contact-edit-${props.id}`}
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.edit}
                    onClick={() => handleEdit(props.id)}
                    permission={[EPermission.MANAGE_LE]}
                  />
                )}
                <Button
                  id={`legal_entity_details_contact-delete-${props.id}`}
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.delete}
                  onClick={() => handleDelete(props.id)}
                  permission={[EPermission.DELETE_LE]}
                />
              </>
            ),
        },
      ];

interface ILegalEntityPreviousNamesSurnamesTableColumns {
  handleEdit: (arg: string) => void;
  handleDelete: (arg: string) => void;
  dateFormat: string;
}

export const legalEntityPreviousNamesSurnamesTableColumns = ({
  handleEdit,
  handleDelete,
  dateFormat,
}: ILegalEntityPreviousNamesSurnamesTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'firstName',
    isSortable: false,
    title: 'legalEntityDetails.previousNamesSurnamesTab.firstName.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'middleNames',
    isSortable: false,
    title: 'legalEntityDetails.previousNamesSurnamesTab.middleName.column',
  },
  {
    id: '04ce93ea-3c2e-439b-a23d-2e9336f9477c',
    key: 'lastName',
    isSortable: false,
    title: 'legalEntityDetails.previousNamesSurnamesTab.surname.column',
  },
  {
    id: '00a44758-b329-4cef-97d8-8680e10207d3',
    key: 'createdDate',
    isSortable: false,
    title: 'legalEntityDetails.previousNamesSurnamesTab.createDate.column',
    dateFormat,
  },
  {
    id: 'c8c974f2-6c38-4239-bd30-ec8b0091ab9e',
    key: 'createdByName',
    isSortable: false,
    title: 'legalEntityDetails.previousNamesSurnamesTab.addedBy.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: '',
    title: 'legalEntityDetails.previousNamesSurnamesTab.action.column',
    isSortable: false,
    isCenterTableHead: true,
    width: '80px',
    align: 'center',
    render: (_data, props) => (
      <>
        <Button
          id={`legal_entity_details_previous_names_surnames-edit-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.edit}
          onClick={() => handleEdit(props.id)}
          permission={[EPermission.MANAGE_LE]}
        />
        <Button
          id={`legal_entity_details_previous_names_surnames-delete-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.delete}
          onClick={() => handleDelete(props.id)}
          permission={[EPermission.MANAGE_LE]}
        />
      </>
    ),
  },
];

export const mapLEContactToDataSource = (
  items: ILegalEntityContactWithAssociation[],
  phoneCode: IParsedDictionary,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    role: item.role,
    legalContactType: item.legalContactType,
    phoneNumber:
      item.phoneNumber &&
      item.phoneNumber.codeDictionaryUniqueId &&
      phoneCode?.[item.phoneNumber.codeDictionaryUniqueId]
        ? `${phoneCode?.[item.phoneNumber.codeDictionaryUniqueId]}${item.phoneNumber.value}`
        : '',
    email: item.email || '-',
    assignedIndividualUniqueId: item.assignedIndividualUniqueId,
    assignedOrganisationUniqueId: item.assignedOrganisationUniqueId,
    assignedOrganisationName: item.assignedOrganisationName,
    assignedIndividualPrefixGroupUniqueId: item.assignedIndividualPrefixGroupUniqueId,
    assignedIndividualVisibilityUniqueId: item.assignedIndividualVisibilityUniqueId,
    assignedOrganisationVisibilityUniqueId: item.assignedOrganisationVisibilityUniqueId,
    assignedOrganisationCategoryUniqueId: item.assignedOrganisationCategoryUniqueId,
    canEdit: item.canEdit,
  }));

export const mapLEPreviousNamesSurnamesToDataSource = (
  items: ILegalEntityPreviousNamesSurnames[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    firstName: item.firstName,
    middleNames: item.middleNames,
    lastName: item.lastName,
    createdDate: item.createdDate,
    createdByName: item.createdByName,
  }));

export const legalEntityGroupFirearmTableColumns = ({
  handleDelete,
  renderStateFieldColor,
}: ILegalEntityAddGroupFirearmTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'groupDetails.firearmListSection.serialNumber.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f511',
    key: 'make',
    title: 'groupDetails.firearmListSection.make.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'model',
    title: 'groupDetails.firearmListSection.model.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'calibre',
    title: 'groupDetails.firearmListSection.calibre.column',
    isSortable: false,
  },
  {
    id: '8923a181-faa9-459c-b5d1-0105f66c14c7',
    key: 'state',
    title: 'groupDetails.firearmListSection.state.column',
    isCenterTableHead: true,
    isSortable: false,
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'address',
    title: 'groupDetails.firearmListSection.address.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f514',
    key: 'gps',
    title: 'groupDetails.firearmListSection.gps.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: '',
    title: 'groupDetails.firearmListSection.action.column',
    isSortable: false,
    isCenterTableHead: true,
    width: '60px',
    align: 'center',
    render: (_data, props) => (
      <Box sx={{ textAlign: 'center' }}>
        <Button
          id={`firearm_group_delete_button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.delete}
          onClick={() => handleDelete(props.id)}
        />
      </Box>
    ),
  },
];

export const mapLEGroupAncillaryListToDataSource = (items: IAncillary[]): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    fireCycleId: item.fireCycleId,
    serialNumber: item.serialNumber,
    lotNumber: item.lotNumber,
    batchNumber: item.batchNumber,
    ancillary: item.ancillaryType,
    ancillaryTypeDictionaryValue: item.ancillaryTypeDictionaryValue,
    ancillaryType: item.ancillarySpecificType,
    manufacturer: item.manufacturer,
    calibre: item.calibre,
    address: item.address,
    gps: formatGPSField(item.latitude, item.longitude),
  }));

export const legalEntityGroupAncillaryTableColumns = ({
  handleDelete,
  hideViewButton,
}: ILegalEntityAncillaryGroupAncillariesTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f502',
    key: 'fireCycleId',
    title: 'addGroup.ancillaryList.fireCycleId.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'addGroup.ancillaryList.serialNumber.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
    key: 'lotNumber',
    title: 'addGroup.ancillaryList.lotNumber.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'batchNumber',
    title: 'addGroup.ancillaryList.batchNumber.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'ancillaryTypeDictionaryValue',
    title: 'addGroup.ancillaryList.ancillary.column',
    isSortable: false,
  },
  {
    id: 'fd7295b8-e1f6-4046-aacb-8282c6f962df',
    key: 'ancillaryType',
    title: 'addGroup.ancillaryList.ancillaryType.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: 'manufacturer',
    title: 'addGroup.ancillaryList.manufacturer.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
    key: 'calibre',
    title: 'addGroup.ancillaryList.calibre.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
    key: 'address',
    title: 'addGroup.ancillaryList.address.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f521',
    key: 'gps',
    title: 'addGroup.ancillaryList.gps.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    isCenterTableHead: true,
    align: 'center',
    width: '80px',
    render: (_data, props) => (
      <Box sx={{ textAlign: 'center', display: 'flex' }}>
        {!hideViewButton && (
          <Link
            to={ERouteLinks.ViewAncillary.replace(':id', props.id).replace(
              ':type',
              props.ancillary,
            )}
            target="_blank"
          >
            <Button
              id={`firearm_group_view_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.view}
            />
          </Link>
        )}
        <Button
          id={`firearm_group_delete_button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.delete}
          onClick={() => handleDelete(props.id)}
        />
      </Box>
    ),
  },
];

export const mapLEGroupFirearmListToDataSource = (
  items: IFirearm[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  caliber: IDictionary[] | null,
  state: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    serialNumber: item.serialNumber,
    make: findInDictionary(item.makeUniqueId, make),
    model: findInDictionary(item.modelUniqueId, model),
    calibre: findInDictionary(item.caliberUniqueId, caliber),
    state: findInDictionary(item.stateUniqueId, state),
    activityUniqueId: item.activityUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    stateUniqueId: item.stateUniqueId,
    address: item.address,
    gps: formatGPSField(item.latitude, item.longitude),
  }));

export const legalEntityGroupSearchFirearmTableColumns = (
  callback: any,
  selectAllCallback: () => void,
  areAllFirearmsSelected: boolean,
  selectedFirearms: IFirearm[],
  renderStateFieldColor: (arg: ITableDataSource) => any,
): ITableColumns[] => [
  {
    id: '0504ca-6b32-482d-b369-304a6f32f5220',
    key: 'isSelected',
    align: 'center',
    title: () => (
      <StyledCheckboxBackgroundWrapper backgroundColor={colors.white}>
        <Checkbox
          id="select-all-firearms-checkbox"
          sx={{
            color: colors.gray400,
            '&.Mui-checked': {
              color: defaultCheckboxStyle.backgroundColorChecked,
            },
            '&.Mui-disabled': {
              color: defaultCheckboxStyle.backgroundColorDisabled,
            },
          }}
          checked={areAllFirearmsSelected}
          onChange={() => selectAllCallback()}
        />
      </StyledCheckboxBackgroundWrapper>
    ),
    isSortable: false,
    width: '20px',
    render: (_data, props: ILegalEntityGroupFirearm) => (
      <Checkbox
        id="select-firearm-checkbox"
        sx={{
          color: colors.gray300,
          '&.Mui-checked': {
            color: colors.primary500,
          },
          '&.MuiCheckbox-root': {
            maxWidth: '16px',
            maxHeight: '16px',
          },
        }}
        checked={selectedFirearms
          .map((selectedFirearm) => selectedFirearm.uniqueId)
          .includes(props.uniqueId)}
        onChange={() => callback(props)}
      />
    ),
  },
  {
    id: 'f0504ca-6b32-482d-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'searchForLegalEntitysFirearm.serialNumber.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f511',
    key: 'make',
    title: 'searchForLegalEntitysFirearm.make.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f512',
    key: 'model',
    title: 'searchForLegalEntitysFirearm.model.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f513',
    key: 'manufacturer',
    title: 'searchForLegalEntitysFirearm.manufacturer.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f514',
    key: 'caliber',
    title: 'searchForLegalEntitysFirearm.calibre.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f516',
    key: 'owner',
    title: 'searchForLegalEntitysFirearm.owner.column',
  },
  {
    id: 'f0504ca6-6b32-482d-b369-304a6f32f515',
    key: 'keeper',
    title: 'searchForLegalEntitysFirearm.keeper.column',
  },
  {
    id: '171fa2de-9f45-11ee-8c90-0242ac120002',
    key: 'state',
    title: 'searchForLegalEntitysFirearm.state.column',
    align: 'center',
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} />
    ),
  },
  {
    id: '171fa2de-9f45-11ee-8090-0242abba0002',
    key: 'address',
    title: 'searchForLegalEntitysFirearm.address.column',
  },
  {
    id: '171fa2de-9f45-11ee-8c90-0242ac120002',
    key: 'gps',
    title: 'searchForLegalEntitysFirearm.gps.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    isCenterTableHead: true,
    width: '80px',
    align: 'center',
    render: (_data, props) => (
      <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
        <Link to={ERouteLinks.ViewRegisteredFirearm.replace(':id', props.id as string)}>
          <Button
            id={`firearm_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            permission={[EPermission.VIEW_FIREARMS]}
          />
        </Link>
        {selectedFirearms.filter((item) => item.uniqueId === props.id).length ? (
          <Button
            id={`firearm_clear_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.clear}
            onClick={() => callback(props)}
          />
        ) : (
          <Button
            id={`firearm_select_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.checkmark}
            onClick={() => callback(props)}
          />
        )}
      </Box>
    ),
  },
];

export const mapLEGroupSearchFirearmToDataSource = (
  items: IFirearm[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  manufacturer: IDictionary[] | null,
  caliber: IDictionary[] | null,
  state: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    isSelected: false,
    id: item.uniqueId,
    uniqueId: item.uniqueId,
    serialNumber: item.serialNumber,
    makeUniqueId: item.makeUniqueId,
    make: findInDictionary(item.makeUniqueId, make),
    modelUniqueId: item.modelUniqueId,
    model: findInDictionary(item.modelUniqueId, model),
    manufacturerUniqueId: item.manufacturerUniqueId,
    manufacturer: findInDictionary(item.manufacturerUniqueId, manufacturer),
    caliberUniqueId: item.caliberUniqueId,
    caliber: findInDictionary(item.caliberUniqueId, caliber),
    keeper: item.keeper,
    owner: item.owner,
    state: findInDictionary(item.stateUniqueId, state),
    legalityUniqueId: item.legalityUniqueId,
    activityUniqueId: item.activityUniqueId,
    stateUniqueId: item.stateUniqueId,
    latitude: item.latitude,
    longitude: item.longitude,
    address: item.address,
    gps: formatGPSField(item.latitude, item.longitude),
  }));

export const mapLegalEntityGroupAncillaryToDataSource = (
  items: IAncillary[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    uniqueId: item.uniqueId,
    artId: item.artId,
    fireCycleId: item.fireCycleId,
    serialNumber: item.serialNumber,
    lotNumber: item.lotNumber,
    batchNumber: item.batchNumber,
    ancillaryType: item.ancillaryType,
    ancillaryTypeDictionaryValue: item.ancillaryTypeDictionaryValue,
    ancillarySpecificType: item.ancillarySpecificType,
    manufacturer: item.manufacturer,
    calibre: item.calibre,
    hostType: item.hostType,
    composition: item.composition,
    factory: item.factory,
    headstampYear: item.headstampYear,
    cartridgeComposition: item.cartridgeComposition,
    proofMark: item.proofMark,
    alphanumericalMarkings: item.alphanumericalMarkings,
    firearmManufacturer: item.firearmManufacturer,
    firearmMake: item.firearmMake,
    firearmModel: item.firearmModel,
    assetId: item.assetId,
  }));

export const legalEntityGroupAncillariesTableColumns = ({
  id,
  groupId,
  type,
  ancillaryOpenDetail,
  setAncillaryOpenDetail,
  hideManagement,
}: ILegalEntityGroupAncillaryTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f501',
    key: 'detail',
    title: '',
    isSortable: false,
    render: (_data, props) => (
      <Box sx={{ textAlign: 'center' }}>
        {ancillaryOpenDetail && ancillaryOpenDetail === props.id ? (
          <Button
            id={`ancillary_details_dropdown_up_button-${props.id}`}
            variant={EButtonVariants.text}
            tableButton
            icon={EIconTypes.dropdownUp}
            onClick={() => setAncillaryOpenDetail('')}
          />
        ) : (
          <Button
            id={`ancillary_details_dropdown_down_button-${props.id}`}
            variant={EButtonVariants.text}
            tableButton
            icon={EIconTypes.dropdownDown}
            onClick={() => setAncillaryOpenDetail(props.id)}
          />
        )}
      </Box>
    ),
  },
  {
    id: 'f0504ca-6b32-4821-b369-304a6f32f510',
    key: 'fireCycleId',
    title: 'ancillaries.list.fireCycleId.column',
  },
  {
    id: 'f0504ca-6b32-4821-b369-304a6f32f511',
    key: 'serialNumber',
    title: 'ancillaries.list.serialNumber.column',
  },
  {
    id: 'f0504ca6-6b32-4822-b369-304a6f32f512',
    key: 'lotNumber',
    title: 'ancillaries.list.lotNumber.column',
  },
  {
    id: 'f0504ca6-6b32-4823-b369-304a6f32f513',
    key: 'batchNumber',
    title: 'ancillaries.list.batchNumber.column',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
    key: 'ancillaryTypeDictionaryValue',
    title: 'ancillaries.list.ancillary.column',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f515',
    key: 'ancillarySpecificType',
    title: 'ancillaries.list.ancillaryType.column',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f516',
    key: 'manufacturer',
    title: 'ancillaries.list.manufacturer.column',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f517',
    key: 'calibre',
    title: 'ancillaries.list.calibre.column',
  },
  {
    id: 'f0504ca6-6b32-4828-b369-304a6f32f518',
    key: '',
    title: 'groups.accordion.action.column',
    isSortable: false,
    isCenterTableHead: true,
    align: 'center',
    width: '80px',
    render: (_data, props) => (
      <Grid sx={{ justifyContent: 'center' }}>
        <Link
          to={ERouteLinks.ViewAncillary.replace(':id', props.id).replace(
            ':type',
            props.ancillaryType,
          )}
        >
          <Button
            id={`ancillary_details_view_button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            permission={EPermission.VIEW_LIMITED_ANCILLARIES}
          />
        </Link>
        {!hideManagement && (
          <Link
            to={`${ERouteLinks.LegalEntityGroupDeleteAncillary.replace(':id', id)
              .replace(':groupId', groupId)
              .replace(':ancillaryId', props.id)
              .replace(
                ':ancillaryType',
                props.ancillaryType.toLowerCase(),
              )}?type=${type}&expand=groups`}
          >
            <Button
              id={`ancillary_details_delete_button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              permission={[EPermission.MANAGE_GROUPS_LE]}
            />
          </Link>
        )}
      </Grid>
    ),
  },
];

export const legalEntityGroupAncillariesDetailsTableColumns = (
  type: string | null | undefined,
): ITableColumns[] => {
  if (type?.toLowerCase() === EArtAncillaryTypeNameLowerCase.COMPONENTS) {
    return [
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f510',
        key: 'artId',
        title: 'ancillaries.componentList.artId.label',
      },
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f511',
        key: 'firearmManufacturer',
        title: 'ancillaries.componentList.firearmManufacturer.label',
      },
      {
        id: 'f0504ca6-6b32-4822-b369-304a6f32f512',
        key: 'firearmMake',
        title: 'ancillaries.componentList.firearmMake.label',
      },
      {
        id: 'f0504ca6-6b32-4823-b369-304a6f32f513',
        key: 'firearmModel',
        title: 'ancillaries.componentList.firearmModel.label',
      },
      {
        id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
        key: 'composition',
        title: 'ancillaries.componentList.composition.label',
      },
    ];
  }
  if (type?.toLowerCase() === EArtAncillaryTypeNameLowerCase.AMMUNITION) {
    return [
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f510',
        key: 'artId',
        title: 'ancillaries.ammunitionList.artId.label',
      },
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f511',
        key: 'factory',
        title: 'ancillaries.ammunitionList.factory.label',
      },
      {
        id: 'f0504ca6-6b32-4822-b369-304a6f32f512',
        key: 'composition',
        title: 'ancillaries.ammunitionList.cartridgeComposition.label',
      },
      {
        id: 'f0504ca6-6b32-4823-b369-304a6f32f513',
        key: 'headstampYear',
        title: 'ancillaries.ammunitionList.headstampYear.label',
      },
      {
        id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
        key: 'alphanumericalMarkings',
        title: 'ancillaries.ammunitionList.alphanumericalMarkings.label',
      },
    ];
  }
  if (type?.toLowerCase() === EArtAncillaryTypeNameLowerCase.ACCESSORY) {
    return [
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f510',
        key: 'artId',
        title: 'ancillaries.accessoryList.artId.label',
      },
      {
        id: 'f0504ca-6b32-4821-b369-304a6f32f511',
        key: 'assetId',
        title: 'ancillaries.accessoryList.assetId.label',
      },
      {
        id: 'f0504ca6-6b32-4822-b369-304a6f32f512',
        key: 'hostType',
        title: 'ancillaries.accessoryList.hostType.label',
      },
      {
        id: 'f0504ca6-6b32-4823-b369-304a6f32f513',
        key: 'composition',
        title: 'ancillaries.accessoryList.composition.label',
      },
      {
        id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
        key: 'proofMark',
        title: 'ancillaries.accessoryList.proofMark.label',
      },
    ];
  }

  return [];
};

export const mapDocumentsToDataSource = (
  items: IDocument[],
  documentsType: IDictionary[] | null,
): ITableDataSource[] =>
  (items || []).map((item, key) => ({
    id: key,
    name: appendExtensionToFilenameFromMimeType(item.name, item.file?.type),
    weaponLicenceDocumentType:
      item.weaponLicenceDocumentType && documentsType
        ? parseDictionary(documentsType, 'name')[item.weaponLicenceDocumentType]
        : '',
    comment: item.comment,
    file: item.file,
  }));

interface IDocumentsTableColumns {
  handleDownload: (file: File) => void;
  handleDelete: (id: number) => void;
}
export const documentsTableColumns = ({
  handleDownload,
  handleDelete,
}: IDocumentsTableColumns): ITableColumns[] => [
  {
    id: '91fc63ee-934e-4158-abe6-528e1a0c02ba',
    key: 'name',
    title: 'legalEntity.licence.documentName.label',
    isSortable: false,
  },
  {
    id: '4ec43361-416f-4dfd-af32-57db12f7ee76',
    key: 'weaponLicenceDocumentType',
    title: 'legalEntity.licence.documentType.label',
    isSortable: false,
  },
  {
    id: '90fe8519-0624-4c1b-9ece-cd907112e307',
    key: 'comment',
    title: 'legalEntity.licence.comment.label',
    isSortable: false,
  },
  {
    id: '92d362bd-83f2-4c30-889f-308917ce68c2',
    key: '',
    title: 'legalEntity.licence.action.column',
    isSortable: false,
    width: '100px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        <Button
          id={`licence_details_attachment_download-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.download}
          onClick={() => handleDownload(props.file)}
        />
        <Button
          id={`licence_details_attachment_delete-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.delete}
          onClick={() => handleDelete(props.id)}
        />
      </>
    ),
  },
];

interface ILegalEntityLicenceTableColumns {
  handleView: (arg: string) => void;
  handleRenew: (arg: string) => void;
  handleAmend: (arg: string) => void;
  dateFormat: string;
  renderLicenceStatusFieldColor: (arg: ITableDataSource) => any;
  t: (key: string) => string;
  setLicenceOpenDetail: (key: string) => void;
  licenceOpenDetail: string;
  leftDaysNotify: number;
  licenceAuthorityValid: boolean;
}

export const legalEntityLicencesTableColumns = ({
  handleView,
  handleRenew,
  handleAmend,
  dateFormat,
  renderLicenceStatusFieldColor,
  t,
  setLicenceOpenDetail,
  licenceOpenDetail,
  leftDaysNotify,
  licenceAuthorityValid,
}: ILegalEntityLicenceTableColumns): ITableColumns[] => [
  {
    id: '4eaea4ee-d36f-400b-895b-231c81f091cf',
    key: 'info',
    width: '64px',
    isSortable: false,
    title: t('licences.info.label'),
    align: 'center',
    justifyContentHead: 'center',
    render: (_data, props) => {
      const leftDaysNumber = props.expiryDate
        ? Number(leftDays(props.expiryDate, t, true))
        : null;
      const textToTooltip = [];
      const isActive = props.statusUniqueId === ELicenceStatusIds.ACTIVE;

      if (props.hasInactiveOwner) {
        textToTooltip.push(t('legalEntity.licenceDetails.hasInactiveOwner.text'));
      }
      if (isActive) {
        if (props.hasAnyFirearmIllegal) {
          textToTooltip.push(t('legalEntity.licenceDetails.hasAnyIllegalFirearm.text'));
        }
        if (props.hasAnyFirearmInactive) {
          textToTooltip.push(t('legalEntity.licenceDetails.hasAnyInactiveFirearm.text'));
        }
        if (props.hasAnyLeDataChanged) {
          textToTooltip.push(t('legalEntity.licenceDetails.hasAnyLeDataChanged.text'));
        }
        if (props.hasAnyFirearmNotAssignedToLegal) {
          textToTooltip.push(
            t('legalEntity.licenceDetails.hasAnyFirearmNotAssignedToLegal.text'),
          );
        }
        if (props.printWarning) {
          textToTooltip.push(t(props.printWarning));
        }
        if (
          props.expiryDate &&
          leftDaysNumber !== null &&
          leftDaysNumber <= leftDaysNotify &&
          !props.renewed
        ) {
          textToTooltip.push(
            leftDaysNumber > 0
              ? `${t('legalEntity.licenceDetails.willExpire.text')} ${leftDaysNumber} ${t(
                  'legalEntity.licenceDetails.willExpire.days',
                )}`
              : t('legalEntity.licenceDetails.willExpireToday.text'),
          );
        }
      }

      return (
        <Grid sx={{ gap: 0.5, display: 'flex' }}>
          <Box sx={{ textAlign: 'start', ml: '-7px' }}>
            {licenceOpenDetail && licenceOpenDetail === props.id ? (
              <Button
                id={`licence_details_dropdown_up_button-${props.id}`}
                variant={EButtonVariants.text}
                icon={EIconTypes.dropdownUp}
                onClick={() => setLicenceOpenDetail('')}
              />
            ) : (
              <Button
                id={`licence_details_dropdown_down_button-${props.id}`}
                variant={EButtonVariants.text}
                icon={EIconTypes.dropdownDown}
                onClick={() => setLicenceOpenDetail(props.id)}
              />
            )}
          </Box>
          {textToTooltip.length > 0 && (
            <Box sx={{ height: '20px', width: '100%', mt: '4px' }}>
              <Tooltip titleArray={textToTooltip}>
                <Icon type={EIconTypes.infoWarning} fontSize={20} />
              </Tooltip>
            </Box>
          )}
        </Grid>
      );
    },
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f801',
    key: 'weaponLicenceNumber',
    title: 'legalEntityDetails.licenceTab.weaponLicenceNumber.column',
    width: '160px',
  },
  {
    id: '18157591-511a-4fe0-9faf-372b0f703525',
    key: 'weaponLicenceName',
    title: 'legalEntityDetails.licenceTab.weaponLicenceName.column',
    width: '346px',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f803',
    key: 'createdDate',
    title: 'legalEntityDetails.licenceTab.createdDate.column',
    width: '120px',
    dateFormat,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f804',
    key: 'startDate',
    title: 'legalEntityDetails.licenceTab.startDate.column',
    width: '120px',
    dateFormat,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f805',
    key: 'expiryDate',
    title: 'legalEntityDetails.licenceTab.expiryDate.column',
    width: '120px',
    dateFormat,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f806',
    key: 'status',
    title: 'legalEntityDetails.licenceTab.status.column',
    width: '120px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Chips text={props.status} variant={renderLicenceStatusFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f807',
    key: '',
    title: 'legalEntityDetails.licenceTab.action.column',
    isSortable: false,
    align: 'center',
    width: '110px',
    isCenterTableHead: true,
    render: (_data, props) => {
      if (!licenceAuthorityValid)
        return (
          <Button
            id={`legal_entity_details_licence-view-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            onClick={() => handleView(props.id)}
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
            ]}
            permissionOr
          />
        );

      return (
        <>
          <Button
            id={`legal_entity_details_licence-view-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            onClick={() => handleView(props.id)}
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
            ]}
            permissionOr
          />
          {props.statusUniqueId === ELicenceStatusIds.ACTIVE && props.canBeVaried && (
            <Button
              id={`legal_entity_details_licence-amend-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.amend}
              onClick={() => handleAmend(props.id)}
              permission={[EPermission.MANAGE_LE]}
            />
          )}
          {props.statusUniqueId === ELicenceStatusIds.ACTIVE && props.canBeRenewed && (
            <Button
              id={`legal_entity_details_licence-renew-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.renew}
              onClick={() => handleRenew(props.id)}
              permission={[EPermission.MANAGE_LE]}
            />
          )}
        </>
      );
    },
  },
];
interface ILicenceAttachmentsDetailTableColumns {
  handleView: (licenceId: string, id: string) => void;
  handleDownload: (licenceId: string, id: string, name: string) => void;
  dateFormat: string;
}

export const LicenceAttachmentsDetailTableColumns = ({
  handleView,
  handleDownload,
  dateFormat,
}: ILicenceAttachmentsDetailTableColumns): ITableColumns[] => [
  {
    id: 'e44713ca-da86-4c55-bdce-ee006577d294',
    key: 'filename',
    title: 'legalEntityDetails.licenceTab.filename.column',
  },
  {
    id: 'bbe720ad-62fe-4005-bc77-9f9a6c0abf70',
    key: 'type',
    title: 'legalEntityDetails.licenceTab.type.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f805',
    key: 'createdDate',
    title: 'legalEntityDetails.licenceTab.createDate.column',
    dateFormat,
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f804',
    key: 'addedBy',
    title: 'legalEntityDetails.licenceTab.addedBy.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f807',
    key: '',
    title: '',
    isSortable: false,
    align: 'right',
    width: '120px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        <Button
          id={`legal_entity_details_licence_attachment-view-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.view}
          onClick={() => handleView(props.licenceId, props.id)}
          permission={[EPermission.MANAGE_LE]}
        />
        <Button
          id={`legal_entity_details_licence_attachment-download-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.download}
          onClick={() => handleDownload(props.licenceId, props.id, props.filename)}
          permission={[EPermission.MANAGE_LE]}
        />
      </>
    ),
  },
];

export const mapLELicencesToDataSource = (
  items: ILegalEntityLicence[],
  weaponLicenceStatus: IParsedDictionary,
  weaponLicenceAttachmentType: IParsedDictionary,
  legalEntityId: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    weaponLicenceName: item.weaponLicenceName,
    weaponLicenceNumber: item.weaponLicenceNumber,
    hasAnyFirearmIllegal: item.hasAnyIllegalFirearm,
    hasAnyFirearmInactive: item.hasAnyInactiveFirearm,
    hasAnyLeDataChanged: item.hasAnyLeDataChanged,
    hasAnyFirearmNotAssignedToLegal: item.hasAnyFirearmNotAssignedToLegal,
    hasInactiveOwner: item.hasInactiveOwner,
    createdDate: item.createdDate,
    startDate: item.startDate,
    expiryDate: item.expiryDate,
    statusUniqueId: item.statusUniqueId,
    canBeRenewed: item.canBeRenewed,
    canBeVaried: item.canBeVaried,
    printWarning: item.printWarning,
    renewed: item.renewed,
    status:
      item.statusUniqueId && item.statusUniqueId in weaponLicenceStatus
        ? weaponLicenceStatus[item.statusUniqueId]
        : '',
    legalEntityId,
    detailsColumns: item.attachments?.map((it: IILegalEntityLicenceAttachment) => ({
      licenceId: item.uniqueId,
      id: it.uniqueId,
      ...it,
      filename: appendExtensionToFilename(it.filename, it.fileType),
      type:
        it.weaponLicenceDocumentTypeUniqueId &&
        it.weaponLicenceDocumentTypeUniqueId in weaponLicenceAttachmentType
          ? weaponLicenceAttachmentType[it.weaponLicenceDocumentTypeUniqueId]
          : '',
    })),
  }));

export const legalEntityAttachmentsTableColumns = ({
  t,
  showAction = false,
  dateFormat,
  type,
  handleView,
  handleDownload,
  handleDelete,
  hasPermissionsAttachments,
  hasPermissionsLicence,
  isLicenceAuthorityValid,
  showLicenceColumns,
}: ILegalEntityAttachmentsTableColumns): ITableColumns[] => [
  {
    id: 'attachment-created-date',
    key: 'createdDate',
    title: t('legalEntityDetails.attachments.fields.createdDate'),
    width: '130px',
    dateFormat,
  },
  {
    id: 'attachment-added-by',
    key: 'addedBy',
    title: t('legalEntityDetails.attachments.fields.addedBy'),
    width: '150px',
  },
  {
    id: 'attachment-document-type',
    key: 'documentTypeUniqueId',
    title: t('legalEntityDetails.attachments.fields.attachmentType'),
    width: '130px',
    isSortable: false,
  },
  {
    id: 'attachment-filename',
    key: 'fileName',
    title: t('legalEntityDetails.attachments.fields.filename'),
    width: '200px',
  },
  {
    id: 'attachment-file-type',
    key: 'fileType',
    title: t('legalEntityDetails.attachments.fields.fileType'),
    width: '100px',
  },
  ...(showLicenceColumns
    ? [
        {
          id: 'attachment-number-id',
          key: 'parentObjectIdNumber',
          title: t('legalEntityDetails.attachments.fields.number'),
          width: '160px',
          align: 'left',
          render: (_data: any, props: any) => {
            if (!props.parentObjectIdNumber || props.parentObjectIdNumber === '') {
              return '-';
            }
            if (props.licenceUniqueId && type && hasPermissionsLicence) {
              return (
                <Link
                  to={`${ERouteLinks.LegalEntityViewLicence.replace(
                    ':id',
                    props.legalUniqueId,
                  ).replace(':licenceId', props.licenceUniqueId)}?type=${type}&from=le`}
                  style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
                >
                  {props.parentObjectIdNumber}
                </Link>
              );
            }
            if (props.caseUniqueId && type && hasPermissionsLicence) {
              return (
                <Link
                  to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                    ':id',
                    props.legalUniqueId,
                  ).replace(':caseId', props.caseUniqueId)}?type=${type}&from=le`}
                  style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
                >
                  {props.parentObjectIdNumber}
                </Link>
              );
            }
            return <span>{props.parentObjectIdNumber}</span>;
          },
        } as ITableColumns,
        {
          id: 'attachment-type',
          key: 'type',
          title: t('legalEntityDetails.attachments.fields.type'),
          width: '100px',
          align: 'right',
          isSortable: false,
          isCenterTableHead: true,
          render: (_data: any, props: any) => (
            <>
              {props.licenceUniqueId && props.licenceUniqueId !== '' && (
                <Chips
                  text={ATTACHMENT_TYPE_LICENCE}
                  variant={EChipsVariants.primary}
                  fullWidth
                />
              )}
              {props.caseUniqueId && props.caseUniqueId !== '' && (
                <Chips
                  text={ATTACHMENT_TYPE_CASE}
                  variant={EChipsVariants.primary}
                  fullWidth
                />
              )}
            </>
          ),
        } as ITableColumns,
        {
          id: 'attachment-licence-code',
          key: 'licenceCode',
          title: t('legalEntityDetails.attachments.fields.licenceCode'),
          width: '120px',
          align: 'left',
        } as ITableColumns,
        {
          id: 'a3d8c5e0-1b9d-0a7e-4f2d-8c5e0b1a3d9f',
          key: 'parentObjectStatusUniqueId',
          title: t('legalEntityDetails.attachments.fields.status'),
          width: '120px',
          isSortable: false,
          isCenterTableHead: true,
          render: (_data: any, props: any) => (
            <>
              {props.licenceUniqueId && props.parentObjectStatusUniqueId && (
                <Chips
                  text={props.objectStatus}
                  variant={getLicenceStatusChipVariant(props.parentObjectStatusUniqueId)}
                  fullWidth
                />
              )}
              {props.caseUniqueId && props.parentObjectStatusUniqueId && (
                <Chips
                  text={props.objectStatus}
                  variant={renderStatusCaseFieldColor({
                    statusUniqueId: props.parentObjectStatusUniqueId,
                  })}
                  fullWidth
                />
              )}
            </>
          ),
        } as ITableColumns,
      ]
    : []),
  ...(showAction
    ? [
        {
          id: 'attachments-actions',
          key: 'actions',
          title: t('legalEntityDetails.attachments.actions.label'),
          width: '110px',
          align: 'center',
          isCenterTableHead: true,
          isSortable: false,
          render: (_data: any, props: any) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2px',
              }}
            >
              <Button
                id={`view-attachment-${props.uniqueId}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.view}
                onClick={() =>
                  handleView?.(
                    props.legalUniqueId,
                    props.uniqueId,
                    props.licenceUniqueId,
                    props.caseUniqueId,
                  )
                }
              />
              <Button
                id={`download-attachment-${props.uniqueId}`}
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.download}
                onClick={() =>
                  handleDownload?.(
                    props.legalUniqueId,
                    props.uniqueId,
                    props.fileName,
                    props.licenceUniqueId,
                    props.caseUniqueId,
                  )
                }
              />
              {!props.licenceUniqueId &&
                !props.caseUniqueId &&
                hasPermissionsAttachments &&
                isLicenceAuthorityValid && (
                  <Button
                    id={`delete-attachment-${props.uniqueId}`}
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.delete}
                    onClick={() => handleDelete?.(props.uniqueId)}
                  />
                )}
            </div>
          ),
        } as ITableColumns,
      ]
    : []),
];

export const mapLEAttachmentsToDataSource = (
  legalEntityAttachments: ILegalAttachmentItem[],
  attachmentDocumentTypeDictionary?: Record<string, string>,
  weaponLicenceStatusDictionary?: Record<string, string>,
  weaponLicenceCaseStatus?: Record<string, string>,
  legalUniqueId?: string,
): ITableDataSource[] =>
  legalEntityAttachments.map((attachment) => ({
    id: attachment.uniqueId,
    uniqueId: attachment.uniqueId,
    legalUniqueId,
    licenceUniqueId: attachment.licenceUniqueId,
    caseUniqueId: attachment.caseUniqueId,
    createdDate: attachment.createdDate,
    addedBy: attachment.addedBy,
    documentTypeUniqueId:
      attachmentDocumentTypeDictionary?.[attachment.documentTypeUniqueId] ||
      attachment.documentTypeUniqueId,
    fileName: appendExtensionToFilename(attachment.fileName, attachment.fileType),
    fileType: attachment.fileType,
    parentObjectIdNumber: attachment.parentObjectIdNumber,
    licenceName: attachment.licenceName,
    licenceCode: attachment.licenceCode,
    parentObjectStatusUniqueId: attachment.parentObjectStatusUniqueId,
    objectStatus:
      weaponLicenceCaseStatus?.[attachment.parentObjectStatusUniqueId] ||
      weaponLicenceStatusDictionary?.[attachment.parentObjectStatusUniqueId] ||
      attachment.parentObjectStatusUniqueId ||
      '',
  }));

interface IGroupedLegalEntityAttachmentsTableColumns {
  t: (key: string) => string;
  setAttachmentOpenDetail: (id: string) => void;
  attachmentOpenDetail: string;
  attachmentDocumentTypeDictionary?: Record<string, string>;
  groupBy?: ELegalAttachmentsGroupBy.ATTACHMENT_TYPE | ELegalAttachmentsGroupBy.NUMBER;
  type?: string | null;
  hasPermissionsLicence: boolean;
}

export const groupedLegalEntityAttachmentsTableColumns = ({
  t,
  setAttachmentOpenDetail,
  attachmentOpenDetail,
  groupBy = ELegalAttachmentsGroupBy.ATTACHMENT_TYPE,
  type,
  hasPermissionsLicence,
}: IGroupedLegalEntityAttachmentsTableColumns): ITableColumns[] => {
  const expandColumn: ITableColumns = {
    id: 'a7f2c9e4-5b3d-4a1e-8f6d-2c9e4b5a7d3f',
    key: 'info',
    width: groupBy === ELegalAttachmentsGroupBy.NUMBER ? '32px' : '38px',
    minWidth: groupBy === ELegalAttachmentsGroupBy.NUMBER ? '32px' : '38px',
    isSortable: false,
    title: '',
    align: 'center',
    render: (_data: any, props: any) => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button
          id={`grouped-attachment-info-${props.id}`}
          variant={EButtonVariants.text}
          icon={
            attachmentOpenDetail === props.id ? EIconTypes.dropdownUp : EIconTypes.dropdownDown
          }
          onClick={() => {
            if (attachmentOpenDetail === props.id) {
              setAttachmentOpenDetail('');
            } else {
              setAttachmentOpenDetail(props.id);
            }
          }}
          sx={{
            '& svg': {
              fontSize: '32px !important',
            },
          }}
        />
      </div>
    ),
  };

  if (groupBy === ELegalAttachmentsGroupBy.ATTACHMENT_TYPE) {
    return [
      expandColumn,
      {
        id: 'b8e3d0f5-6c4e-5b2f-9a7e-3d0f5c6b8e4a',
        key: 'attachmentType',
        title: t('legalEntityDetails.attachments.fields.attachmentType'),
        width: '800px',
        isSortable: false,
      },
      {
        id: 'c9f4e1a6-7d5f-6c3a-0b8f-4e1a6d7c9f5b',
        key: 'fileCount',
        title: t('legalEntityDetails.attachments.fields.numberOfFiles'),
        width: '400px',
        isSortable: false,
        align: 'right',
        render: (_data: any, props: any) =>
          `${props.fileCount} ${t('legalEntityDetails.attachments.fields.filesUnit')}`,
      },
    ];
  }

  return [
    expandColumn,
    {
      id: 'd0a5f2b7-8e6a-7d4b-1c9a-5f2b7e8d0a6c',
      key: 'parentObjectIdNumber',
      title: t('legalEntityDetails.attachments.fields.number'),
      width: '164px',
      isSortable: false,
      render: (_data: any, props: any) => {
        if (!props.parentObjectIdNumber || props.parentObjectIdNumber === '') {
          return '-';
        }
        if (props.licenceUniqueId && type && hasPermissionsLicence) {
          return (
            <Link
              to={`${ERouteLinks.LegalEntityViewLicence.replace(
                ':id',
                props.legalUniqueId,
              ).replace(':licenceId', props.licenceUniqueId)}?type=${type}&from=le`}
              style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
            >
              {props.parentObjectIdNumber}
            </Link>
          );
        }
        if (props.caseUniqueId && type && hasPermissionsLicence) {
          return (
            <Link
              to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                ':id',
                props.legalUniqueId,
              ).replace(':caseId', props.caseUniqueId)}?type=${type}&from=le`}
              style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
            >
              {props.parentObjectIdNumber}
            </Link>
          );
        }
        return <span>{props.parentObjectIdNumber}</span>;
      },
    },
    {
      id: 'e1b6a3c8-9f7b-8e5c-2d0b-6a3c8f9e1b7d',
      key: '',
      title: t('legalEntityDetails.attachments.fields.licenceName'),
      width: '200px',
      isSortable: false,
    },
    {
      id: 'f2c7b4d9-0a8c-9f6d-3e1c-7b4d9a0f2c8e',
      key: 'licenceCode',
      title: t('legalEntityDetails.attachments.fields.licenceCode'),
      width: '150px',
      isSortable: false,
    },
    {
      id: 'a1f3c5b7-2d9e-4a6f-8b1d-3c5b7e9a1f4d',
      key: 'type',
      title: t('legalEntityDetails.attachments.fields.type'),
      width: '100px',
      isSortable: false,
      isCenterTableHead: true,
      render: (_data: any, props: any) => (
        <>
          {props.licenceUniqueId && props.licenceUniqueId !== '' && (
            <Chips text={ATTACHMENT_TYPE_LICENCE} variant={EChipsVariants.primary} fullWidth />
          )}
          {props.caseUniqueId && props.caseUniqueId !== '' && (
            <Chips text={ATTACHMENT_TYPE_CASE} variant={EChipsVariants.primary} fullWidth />
          )}
        </>
      ),
    },
    {
      id: 'a3d8c5e0-1b9d-0a7e-4f2d-8c5e0b1a3d9f',
      key: 'parentObjectStatusUniqueId',
      title: t('legalEntityDetails.attachments.fields.status'),
      width: '120px',
      isSortable: false,
      isCenterTableHead: true,
      render: (_data: any, props: any) => (
        <>
          {props.licenceUniqueId &&
            props.parentObjectStatusUniqueId &&
            props.objectStatus !== '-' && (
              <Chips
                text={props.objectStatus}
                variant={getLicenceStatusChipVariant(props.parentObjectStatusUniqueId)}
                fullWidth
              />
            )}
          {props.caseUniqueId &&
            props.parentObjectStatusUniqueId &&
            props.objectStatus !== '-' && (
              <Chips
                text={props.objectStatus}
                variant={renderStatusCaseFieldColor({
                  statusUniqueId: props.parentObjectStatusUniqueId,
                })}
                fullWidth
              />
            )}
        </>
      ),
    },
    {
      id: 'b4e9d6f1-2c0e-1b8f-5a3e-9d6f1c2b4e0a',
      key: 'fileCount',
      title: t('legalEntityDetails.attachments.fields.numberOfFiles'),
      width: '120px',
      isSortable: false,
      align: 'right',
      render: (_data: any, props: any) =>
        `${props.fileCount} ${t('legalEntityDetails.attachments.fields.filesUnit')}`,
    },
  ];
};

interface IGroupedAttachmentDetailTableColumns {
  t: (key: string) => string;
  dateFormat: string;
  type?: string | null;
  handleView?: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDownload?: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    fileName: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDelete?: (attachmentUniqueId: string) => void;
  groupBy?: ELegalAttachmentsGroupBy.ATTACHMENT_TYPE | ELegalAttachmentsGroupBy.NUMBER;
  attachmentDocumentTypeDictionary?: Record<string, string>;
  hasPermissionsAttachments: boolean;
  hasPermissionsLicence: boolean;
}

export const groupedAttachmentDetailTableColumns = ({
  t,
  dateFormat,
  type,
  handleView,
  handleDownload,
  handleDelete,
  groupBy = ELegalAttachmentsGroupBy.ATTACHMENT_TYPE,
  hasPermissionsAttachments,
  hasPermissionsLicence,
}: IGroupedAttachmentDetailTableColumns): ITableColumns[] => {
  const filenameColumn: ITableColumns = {
    id: 'c5f0a7g2-3d1f-7e4a-6b8c-0a7g2d3c5f1h',
    key: 'fileName',
    title: t('legalEntityDetails.attachments.fields.filename'),
    width: '200px',
  };

  const createdDateColumn: ITableColumns = {
    id: 'd6a1h8b3-4e2a-8f5b-7c9d-1h8b3e4d6a2i',
    key: 'createdDate',
    title: t('legalEntityDetails.attachments.fields.createdDate'),
    width: '130px',
    dateFormat,
  };

  const addedByColumn: ITableColumns = {
    id: 'e7b2i9c4-5f3b-9a6c-8d0e-2i9c4f5e7b3j',
    key: 'addedBy',
    title: t('legalEntityDetails.attachments.fields.addedBy'),
    width: '150px',
  };

  const actionsColumn: ITableColumns = {
    id: 'f8c3j0d5-6a4c-0b7d-9e1f-3j0d5a6f8c4k',
    key: 'actions',
    title: t('legalEntityDetails.attachments.actions.label'),
    width: '60px',
    minWidth: '60px',
    align: 'left',
    isSortable: false,
    render: (_data: any, props: any) => (
      <div
        style={{
          width: '60px',
          maxWidth: '60px',
          minWidth: '60px',
          display: 'flex',
          gap: '4px',
        }}
      >
        <Button
          id={`view-grouped-attachment-${props.uniqueId}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.view}
          onClick={() =>
            handleView?.(
              props.legalUniqueId,
              props.uniqueId,
              props.licenceUniqueId,
              props.caseUniqueId,
            )
          }
        />
        <Button
          id={`download-grouped-attachment-${props.uniqueId}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.download}
          onClick={() =>
            handleDownload?.(
              props.legalUniqueId,
              props.uniqueId,
              props.fileName,
              props.licenceUniqueId,
              props.caseUniqueId,
            )
          }
        />
        {!props.licenceUniqueId && !props.caseUniqueId && hasPermissionsAttachments && (
          <Button
            id={`delete-grouped-attachment-${props.uniqueId}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.delete}
            onClick={() => handleDelete?.(props.uniqueId)}
          />
        )}
      </div>
    ),
  };

  if (groupBy === ELegalAttachmentsGroupBy.NUMBER) {
    return [
      filenameColumn,
      {
        id: 'a9d4k1e6-7b5d-1c8e-0f2a-4k1e6b7a9d5l',
        key: 'attachmentType',
        title: t('legalEntityDetails.attachments.fields.attachmentType'),
        width: '130px',
      },
      createdDateColumn,
      addedByColumn,
      actionsColumn,
    ];
  }

  return [
    filenameColumn,
    createdDateColumn,
    addedByColumn,
    {
      id: 'b0e5l2f7-8c6e-2d9f-1a3b-5l2f7c8b0e6m',
      key: 'parentObjectIdNumber',
      title: t('legalEntityDetails.attachments.fields.number'),
      width: '160px',
      render: (_data: any, props: any) => {
        if (!props.parentObjectIdNumber || props.parentObjectIdNumber === '') {
          return '-';
        }
        if (props.licenceUniqueId && type && hasPermissionsLicence) {
          return (
            <Link
              to={`${ERouteLinks.LegalEntityViewLicence.replace(
                ':id',
                props.legalUniqueId,
              ).replace(':licenceId', props.licenceUniqueId)}?type=${type}&from=le`}
              style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
            >
              {props.parentObjectIdNumber}
            </Link>
          );
        }
        if (props.caseUniqueId && type && hasPermissionsLicence) {
          return (
            <Link
              to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                ':id',
                props.legalUniqueId,
              ).replace(':caseId', props.caseUniqueId)}?type=${type}&from=le`}
              style={{ color: colors.primary500, fontWeight: 600, textDecoration: 'none' }}
            >
              {props.parentObjectIdNumber}
            </Link>
          );
        }
        return <span>{props.parentObjectIdNumber}</span>;
      },
    },
    {
      id: 'c1f6m3a8-9d7f-3e0a-2b4c-6m3a8d9c1f7n',
      key: 'licenceCode',
      title: t('legalEntityDetails.attachments.fields.licenceCode'),
      width: '120px',
    },
    {
      id: 'b2g4d6c8-3e0a-5b7f-9c2e-4d6c8a0b2g5h',
      key: 'type',
      title: t('legalEntityDetails.attachments.fields.type'),
      width: '100px',
      isSortable: false,
      isCenterTableHead: true,
      render: (_data: any, props: any) => (
        <>
          {props.licenceUniqueId && props.licenceUniqueId !== '' && (
            <Chips text={ATTACHMENT_TYPE_LICENCE} variant={EChipsVariants.primary} fullWidth />
          )}
          {props.caseUniqueId && props.caseUniqueId !== '' && (
            <Chips text={ATTACHMENT_TYPE_CASE} variant={EChipsVariants.primary} fullWidth />
          )}
        </>
      ),
    },
    {
      id: 'a3d8c5e0-1b9d-0a7e-4f2d-8c5e0b1a3d9f',
      key: 'parentObjectStatusUniqueId',
      title: t('legalEntityDetails.attachments.fields.status'),
      width: '120px',
      isSortable: false,
      isCenterTableHead: true,
      render: (_data: any, props: any) => (
        <>
          {props.licenceUniqueId &&
            props.parentObjectStatusUniqueId &&
            props.objectStatus !== '-' && (
              <Chips
                text={props.objectStatus}
                variant={getLicenceStatusChipVariant(props.parentObjectStatusUniqueId)}
                fullWidth
              />
            )}
          {props.caseUniqueId &&
            props.parentObjectStatusUniqueId &&
            props.objectStatus !== '-' && (
              <Chips
                text={props.objectStatus}
                variant={renderStatusCaseFieldColor({
                  statusUniqueId: props.parentObjectStatusUniqueId,
                })}
                fullWidth
              />
            )}
        </>
      ),
    },
    actionsColumn,
  ];
};

export const mapGroupedAttachmentsToDataSource = (
  groupedData: Record<string, any[]>,
  attachmentDocumentTypeDictionary?: Record<string, string>,
): ITableDataSource[] =>
  Object.entries(groupedData).map(([attachmentTypeId, attachments]) => ({
    id: attachmentTypeId,
    attachmentTypeId,
    attachmentType: attachmentDocumentTypeDictionary?.[attachmentTypeId] || attachmentTypeId,
    fileCount: attachments.length,
    attachments,
  }));

export const mapGroupedAttachmentDetailsToDataSource = (
  attachments: any[],
  weaponLicenceStatusDictionary?: Record<string, string>,
  weaponLicenceCaseStatus?: Record<string, string>,
  legalUniqueId?: string,
  attachmentDocumentTypeDictionary?: Record<string, string>,
): ITableDataSource[] =>
  attachments.map((attachment) => ({
    id: attachment.uniqueId,
    uniqueId: attachment.uniqueId,
    legalUniqueId,
    licenceUniqueId: attachment.licenceUniqueId,
    caseUniqueId: attachment.caseUniqueId,
    createdDate: attachment.createdDate,
    addedBy: attachment.addedBy,
    fileName: appendExtensionToFilename(attachment.fileName, attachment.fileType),
    fileType: attachment.fileType,
    attachmentType:
      attachmentDocumentTypeDictionary?.[attachment.documentTypeUniqueId] ||
      attachment.documentTypeUniqueId ||
      '',
    parentObjectIdNumber: attachment.parentObjectIdNumber,
    licenceName: attachment.licenceName,
    licenceCode: attachment.licenceCode,
    parentObjectStatusUniqueId: attachment.parentObjectStatusUniqueId,
    objectStatus:
      weaponLicenceStatusDictionary?.[attachment.parentObjectStatusUniqueId] ||
      weaponLicenceCaseStatus?.[attachment.parentObjectStatusUniqueId] ||
      attachment.parentObjectStatusUniqueId ||
      '',
  }));

export const mapLECasesToDataSource = (
  items: ICaseForLe[],
  shortDateFormat: string,
  weaponLicenceCaseStatus: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    caseNumber: item.caseNumber,
    licenceTemplateCode: item.licenceTemplateCode,
    licenceTemplateName: item.licenceTemplateName,
    licenceTaskName: item.licenceTaskName,
    createdDate: formatDate(item.createdDate, shortDateFormat),
    status: findInDictionary(item.statusUniqueId, weaponLicenceCaseStatus),
    licenceAuthorityUniqueId: item.licenceAuthorityUniqueId,
    statusUniqueId: item.statusUniqueId,
    assignedTo: item.assignedTo,
    assignedToDisplayUniqueId: item.assignedToDisplayUniqueId,
    assignedToUniqueId: item.assignedToUniqueId,
  }));

interface IlegalEntityCasesTableColumns {
  isApplication: boolean;
  isOtherRequest: boolean;
  handleView: (id: string) => void;
  handleCreateLicence: (id: string) => void;
  handleAssign: (id: string) => void;
  canAssignCase: (licenceAuthorityUniqueId: string, statusUniqueId: string) => boolean;
  showDetailsCases: boolean;
  t: any;
}

export const legalEntityCasesTableColumns = ({
  isApplication,
  isOtherRequest,
  handleView,
  handleCreateLicence,
  handleAssign,
  canAssignCase,
  showDetailsCases,
  t,
}: IlegalEntityCasesTableColumns): ITableColumns[] => {
  return [
    {
      id: 'd0a5f2b7-8e6a-7d4b-1c9a-5f2b7e8d0a6c',
      key: 'caseNumber',
      title: 'caseManagement.cases.caseId.label',
      width: '180px',
    },
    ...(isOtherRequest
      ? [
          {
            id: 'e1b6a3c8-9f7b-8e5c-2d0b-6a3c8f9e1b7d',
            key: 'licenceTaskName',
            title: 'caseManagement.cases.taskName.label',
          },
        ]
      : [
          {
            id: 'e1b6a3c8-9f7b-8e5c-2d0b-6a3c8f9e1b7d',
            key: 'licenceTemplateCode',
            title: 'caseManagement.cases.licenceCodeName.label',
            render: (_data: any, props: any) =>
              props.licenceTemplateCode && props.licenceTemplateName
                ? `${props.licenceTemplateCode} ${props.licenceTemplateName}`
                : '-',
          },
        ]),
    {
      id: 'f2c7b4d9-0a8c-9f6d-3e1c-7b4d9a0f2c8e',
      key: 'createdDate',
      title: 'caseManagement.cases.createDate.label',
      align: 'center',
      isCenterTableHead: true,
      width: '140px',
    },
    {
      id: 'a1f3c5b7-2d9e-4a6f-8b1d-3c5b7e9a1f4d',
      key: 'status',
      title: 'caseManagement.cases.status.label',
      isCenterTableHead: true,
      align: 'center',
      width: '140px',
      render: (_data, props) => (
        <Chips text={props.status} variant={renderStatusCaseFieldColor(props)} fullWidth />
      ),
    },
    {
      id: 'a3d8c5e0-1b9d-0a7e-4f2d-8c5e0b1a3d9f',
      key: 'assignedTo',
      title: 'caseManagement.cases.assignedTo.label',
      render: (_data, props) =>
        props.assignedTo ? `${props.assignedTo} (${props.assignedToDisplayUniqueId})` : '-',
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f007',
      key: '',
      title: 'caseManagement.cases.actions.label',
      isSortable: false,
      align: 'center',
      width: '90px',
      isCenterTableHead: true,
      render: (_data, props) => {
        const canCreateLicence = props.statusUniqueId === CaseStatus.GRANTED;
        return (
          <>
            {showDetailsCases && (
              <MenuButton
                id={`menu_${props.id}`}
                items={[
                  {
                    name: t('caseManagement.cases.actions.details.label'),
                    icon: EIconTypes.view,
                    handler: () => {
                      handleView(props.id);
                    },
                  },
                  ...(isApplication && canCreateLicence
                    ? [
                        {
                          name: t('caseManagement.cases.actions.createLicence.label'),
                          icon: EIconTypes.createLicence,
                          handler: () => {
                            handleCreateLicence(props.id);
                          },
                        },
                      ]
                    : []),
                  ...((isApplication || isOtherRequest) &&
                  canAssignCase(props.licenceAuthorityUniqueId, props.statusUniqueId)
                    ? [
                        {
                          name: t('caseManagement.cases.actions.assignTo.label'),
                          icon: EIconTypes.assignTo,
                          handler: () => {
                            handleAssign(props.id);
                          },
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </>
        );
      },
    },
  ];
};

export const prepareCustomFieldPayload = (
  data: any,
  customFieldsLicence: ICustomFieldByObjectType[],
): ICustomFieldForm[] => {
  const customFieldPayload: ICustomFieldForm[] = [];

  Object.keys(data).map((key) => {
    const customField = customFieldsLicence.find((item) => item.name === key);
    if (customField) {
      const customFieldItem: ICustomFieldForm = {
        customFieldUniqueId: customField.uniqueId,
      };
      if (customField.customFieldType === ECustomFieldType.TEXT) {
        if (data[key] === undefined || data[key] === null) {
          customFieldItem.text = undefined;
        } else if (data[key].trim() === '') {
          customFieldItem.text = undefined;
        } else {
          customFieldItem.text = data[key];
        }
      } else if (customField.customFieldType === ECustomFieldType.BOOL) {
        if (data[key] === '') {
          customFieldItem.bool = null;
        } else {
          customFieldItem.bool = data[key] === '1';
        }
      } else if (customField.customFieldType === ECustomFieldType.DATE) {
        customFieldItem.date = data[key] ? formatDateTimeToIso(data[key]) : null;
      }
      customFieldPayload.push(customFieldItem);
      delete data[key];
    }
    return false;
  });

  return customFieldPayload;
};
